/**
 * Editor shell — the paint loop.
 *
 * Paint on the flat template, see it land on the body. That round trip is the
 * product, and this file is the whole of it:
 *
 *   pointer → template pixels → stroke → live canvas → viewer.invalidate(rect)
 *
 * Two rules it exists to enforce:
 *
 *   1. three.js is ~635 kB and lives behind `await import()`. Nothing here may
 *      import the viewer statically — only its types, which erase at build time.
 *
 *   2. Below 1024px the 3D pane is not displayed, so the viewer is never mounted
 *      and the chunk is never fetched. The responsive rule and the performance
 *      budget are the same rule.
 */
import { toHex } from "../../shared/colour";
import {
  CANVAS,
  type FacePlacement,
  type GarmentType,
  type Project,
  type RegionId,
} from "../../shared/format";
import { nearestFace, scaleToFit } from "../../shared/placement";
import { GUTTER_PX, REGIONS, regionAt } from "../../shared/regions";
import type { SeamMap } from "../../shared/seam-map";
import {
  allows,
  allowsAt,
  describe as describeSelection,
  EMPTY,
  groupOf,
} from "../../shared/selection";
import { identity, onIdentityChange } from "../auth/session";
import { setSaveState } from "../hud/app-bar";
import { replace } from "../hud/dom";
import { openUpsell } from "../hud/upsell-modal";
import { createGhostController, type GhostController } from "../viewer/ghost";
import { webglSupported } from "../viewer/support";
import type { PartGroup, ViewerHandle } from "../viewer/types";
import { installAutosave, openIntentFrom } from "./autosave";
import { blurInto, canBlur, openBlurDialog, restorePixels } from "./blur";
import type { BodyPicker } from "./body-picker";
import { mountBrushRing } from "./brush-ring";
import { createColourPicker } from "./colour-picker";
import { canTint, isFabricLocked, lockedMessage } from "./fabric-entitlement";
import {
  applyFabric,
  loadTile,
  MAX_TILE_PX,
  MIN_TILE_PX,
  previewFor,
  refillLayer,
} from "./fabric-fill";
import { createFabricPicker, displayName } from "./fabric-picker";
import {
  differs,
  type FabricSource,
  fabricOf,
  restoreFabric,
  withTilePx,
  withTint,
} from "./fabric-source";
import { mountFaceList } from "./face-list";
import { type FaceSelect, mountFaceSelect } from "./face-select";
import { faceLabel } from "./faces";
import { faceFillRect, floodMask, partFaces, unionRect } from "./fill";
import { clampToCanvas, fillBounds, fillWithMirror } from "./fill-mirror";
import { mountFootprintOverlay } from "./footprint-overlay";
import { groupFor, groupsOf } from "./garment";
import { createHistory, type Edit, type HistoryOptions } from "./history";
import type { ImportReport, ImportResult } from "./import";
import { importFile, linkLiveFile, liveLinkSupported, wireDropTarget } from "./import";
import { type Binding, installKeys } from "./keys";
import {
  createLayerStack,
  type LayerStackOptions,
  type StrokeMode,
  strokeCompositeMode,
} from "./layers";
import type { MirrorControls } from "./mirror-controls";
import { type ModeChips, mountModeChips } from "./mode-chips";
import { createPainter, type DirtyRect } from "./paint";
import { mountLayersPanel } from "./panel";
import {
  decodeImage,
  initialPlacement,
  isPlaceableImage,
  layerNameFor,
  PLACEABLE_TYPES,
} from "./place";
import { canMakeRankSet, planRankSet, rankSetZipName } from "./rank-set";
import { createSeamBreaks, type SeamBreaks } from "./seam-breaks";
import { mountSeamOverlay, type SeamOverlay } from "./seam-overlay";
import { shortcutsSheet } from "./shortcuts-sheet";
import { hydrate } from "./snapshot";
import type { SymmetryOverlay } from "./symmetry-overlay";
import { mountSymmetryOverlay } from "./symmetry-overlay";
import { mountTips, type Tips } from "./tip";
import { installToaster, type ToastTone, toast } from "./toast";
import { mountToolOptions } from "./tool-options";
import { createToolState, type ToolId } from "./tools";
import { mountTransformOverlay, type TransformOverlay } from "./transform-overlay";
import { countUsage } from "./usage";
import { createVersionsPanel } from "./versions-panel";
import { mountViewController, type ViewController } from "./view-controller";

const paintCanvas = document.querySelector<HTMLCanvasElement>("[data-paint]");
const mount = document.querySelector<HTMLElement>("[data-viewer-mount]");
const label = document.querySelector<HTMLElement>("[data-viewer-label]");

/** Matches the ladder in docs/decisions/responsive-layout.md. */
const CAN_SHOW_3D = "(width >= 1024px)";

let viewer: ViewerHandle | null = null;
let picker: BodyPicker | null = null;
/**
 * Mirroring: the live toggle and the one-shot command, as one group in the
 * tool rail, plus the surface the guide draws on.
 *
 * Bound to the viewer's lifetime rather than the canvas's, because both are
 * meaningless without a mirror map and the map comes from the body. Null below
 * 1024px, where there is no 3D pane and so no map.
 */
let mirrorControls: MirrorControls | null = null;
let symmetryOverlay: SymmetryOverlay | null = null;
let seamOverlay: SeamOverlay | null = null;
let seamBreaks: SeamBreaks | null = null;
/**
 * Whether the seam marks are shown.
 *
 * On by default. It draws nothing at all on a garment whose folds agree, which
 * is the common case, so there is no cost to leaving it on — and it is the one
 * thing about a Roblox template that no other tool can report. Off by default
 * would put the differentiator behind a switch nobody knows to flip.
 *
 * Deliberately NOT the reasoning that defaulted `wrapFolds` off. That mode
 * changed where paint landed; this one changes nothing about the document or
 * the file. A lens, not a mode.
 */
let seamMarksOn = true;

/** Labels for the rail's icon-only controls. Mounted with the rail itself. */
let tips: Tips | null = null;

/** Which faces may be painted. A tool mode, so it is never persisted. */
let faceSelect: FaceSelect | null = null;
/**
 * Fades the parts blocking the faces you selected.
 *
 * Rebuilt with the viewer, because it holds the set it has faded and that set
 * means nothing against a different body's meshes.
 */
let ghost: GhostController | null = null;

/*
  A selection changes what every stroke does, so it gets a chip for the same
  reason symmetry does — and its label carries the selection itself, because
  "3 faces" and "Right arm" are different enough to be worth reading.
*/
/*
  The picker's controls, on the canvas rather than in a popover.

  Principle 3 of interaction-and-devices.md — options appear when the tool is
  chosen — and principle 2: every gesture has a button. The granularity one is
  the load-bearing member. Taking a whole limb was reachable only by holding Alt
  or Shift, and a tablet has neither, so on the device this app is aimed at
  "select the right arm" was six taps or nothing.

  On the canvas rather than in the tool-options popover because a popover is
  travel: it opens from the rail, which is the place these exist to stop you
  going back to.
*/
const GRAIN_CHIP = () => ({
  id: "faces-grain",
  label: faceSelect?.granularity() === "part" ? "Part" : "Face",
  hint:
    faceSelect?.granularity() === "part"
      ? "A tap takes the whole part. Click for single faces, or hold Alt for one."
      : "A tap takes one face. Click for whole parts, or hold Alt for one.",
  icon:
    '<svg class="mc-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" ' +
    'stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true">' +
    (faceSelect?.granularity() === "part"
      ? '<rect x="4" y="4" width="16" height="16" rx="2"/>'
      : '<rect x="4" y="4" width="7" height="7" rx="1"/><rect x="13" y="13" width="7" height="7" rx="1"/>') +
    "</svg>",
  onSelect: () => {
    faceSelect?.setGranularity(faceSelect.granularity() === "part" ? "face" : "part");
    refreshFaceChips();
  },
});

const ALL_CHIP = {
  id: "faces-all",
  label: "All",
  hint: "Select every face (Cmd+A)",
  onSelect: () => faceSelect?.selectAll(),
};

const INVERT_CHIP = {
  id: "faces-invert",
  label: "Invert",
  hint: "Swap what is selected for what is not (Shift+Cmd+I)",
  onSelect: () => faceSelect?.invertSelection(),
};

const CLEAR_CHIP = {
  id: "faces-clear",
  label: "Clear",
  hint: "Paint anywhere again (Cmd+D)",
  onSelect: () => faceSelect?.clear(),
};

/** Show or hide the picker's controls, in the order they are reached for. */
function refreshFaceChips() {
  const on = tools.get().active === "faces";
  modeChips?.set(GRAIN_CHIP(), on);
  modeChips?.set(ALL_CHIP, on);
  modeChips?.set(INVERT_CHIP, on);
  modeChips?.set(CLEAR_CHIP, on);
  modeChips?.set(DONE_CHIP, on);
}

/*
  The way back to painting, beside the chip that says why you are not.

  Shown only while the picker is active, so the strip stays empty when nothing
  is going on — an idle chip strip is the resting state, and anything in it is
  something to know about.
*/
const DONE_CHIP = {
  id: "faces-done",
  label: "Done",
  hint: "Finish selecting and go back to painting (Enter)",
  icon:
    '<svg class="mc-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" ' +
    'stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" ' +
    'aria-hidden="true"><path d="m5 13 4 4L19 7"/></svg>',
  onSelect: () => doneSelecting(),
};

const FACES_CHIP = {
  id: "faces",
  label: "Faces",
  hint: "Painting is limited to the selected faces. Click to clear, or press Escape.",
  icon:
    '<svg class="mc-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" ' +
    'stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true">' +
    '<path d="M4 8V5a1 1 0 0 1 1-1h3"/><path d="M16 4h3a1 1 0 0 1 1 1v3"/>' +
    '<path d="M20 16v3a1 1 0 0 1-1 1h-3"/><path d="M8 20H5a1 1 0 0 1-1-1v-3"/></svg>',
  onClear: () => faceSelect?.clear(),
};

/*
  Where "Done" goes back to.

  The tool you were on before you reached for the picker — brush or eraser —
  because selecting faces is a SETUP step, not a destination. Sending everyone
  back to the brush would be wrong for the person who was erasing.
*/
let toolBeforeFaces: ToolId = "brush";
/**
 * The tool to come back to once a colour has been picked up.
 *
 * Same idea as `toolBeforeFaces`: sampling is a thing you do *during* something
 * else. Making it a mode you have to leave by hand means every colour costs
 * three actions — pick the dropper, tap, pick the brush again.
 */
let toolBeforeSampling: ToolId = "brush";
/** The rail's colour picker, once mounted. See the eyedropper. */
let colourPicker: ReturnType<typeof createColourPicker> | null = null;

/**
 * Finish selecting and go back to painting, keeping the selection.
 *
 * The gap this closes: clearing a selection while drawing already costs
 * nothing, because the chip's x floats over the canvas rather than living in
 * the rail. Getting back to DRAWING did not — it meant travelling to the rail
 * and finding the brush again, every time.
 */
function doneSelecting() {
  if (tools.get().active !== "faces") return;
  tools.select(toolBeforeFaces);
}

/** Shirt or pants, for labelling a limb as an arm or a leg. */
function garmentForLabel(): "shirt" | "pants" {
  return garment === "pants" ? "pants" : "shirt";
}

/**
 * Active-mode chips, over the 2D canvas rather than the preview.
 *
 * A mode that changes what a stroke does belongs next to where the stroke
 * happens. Symmetry was only visible in the 3D pane's caption, which is the
 * wrong surface and can be scrolled away from entirely — so the state was
 * invisible exactly when it mattered.
 */
const modeChips: ModeChips | null = paintCanvas?.parentElement
  ? mountModeChips(paintCanvas.parentElement)
  : null;

const SYMMETRY_CHIP = {
  id: "symmetry",
  label: "Symmetry",
  hint: "Strokes mirror to the other side. Click to turn off, or press S.",
  icon:
    '<svg class="mc-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" ' +
    'stroke="currentColor" stroke-width="2.2" stroke-linecap="round" ' +
    'aria-hidden="true"><path d="M12 3v18"/><path d="M7 8 3 12l4 4"/>' +
    '<path d="m17 8 4 4-4 4"/></svg>',
  onClear: () => mirrorControls?.set(false),
};

/*
  There was a "Seam fill" chip here, for a preview that no longer exists. The
  export sheet's "Clean edges" step is where that question is asked now, and a
  chip reporting a mode nothing can enter is worse than no chip.
*/

/*
  Painting across joints changes where every stroke can land, so it gets a chip
  for the same reason symmetry does: a mode that changes what a stroke does must
  not be invisible from where the stroke happens.
*/
/**
 * Shown while a stroke may carry around a limb.
 *
 * It has a chip for the reason the toggle exists: measured, a 40px brush on an
 * R6 arm — whose face is 64 texels across — puts over a thousand texels on each
 * neighbouring face from a dab in the dead centre. That is a lot of paint
 * arriving somewhere the pointer never went, and a mode doing that silently is
 * indistinguishable from a bug. It was reported as one.
 */
const WRAP_CHIP = {
  id: "wrap-folds",
  label: "Around folds",
  hint: "Strokes carry around the limb onto the faces beside it. Click to keep them on one face.",
  icon:
    '<svg class="mc-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" ' +
    'stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true">' +
    '<path d="M5 8h4a3 3 0 0 1 3 3v2a3 3 0 0 0 3 3h4"/></svg>',
  onClear: () => mirrorControls?.setWrapFolds(false),
};

const JOINTS_CHIP = {
  id: "cross-parts",
  label: "Across joints",
  hint: "Strokes cross joints onto other body parts. Folds are always crossed. Click to keep strokes on one part.",
  icon:
    '<svg class="mc-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" ' +
    'stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true">' +
    '<path d="M4 9h5a3 3 0 0 1 3 3a3 3 0 0 0 3 3h5"/></svg>',
  onClear: () => mirrorControls?.setCrossParts(false),
};

/**
 * Which body the design is being made for.
 *
 * Module scope, not the picker's, because it outlives it: the picker is
 * destroyed whenever the viewer unmounts, which happens on every drop below
 * 1024px. The export seam-corrects against this, so reading it off the picker
 * would mean the same design exporting differently after a window resize.
 */
let bodyId = "r6-classic";

/**
 * The seam pairing for the body and garment on screen, or null until it lands.
 *
 * Held here rather than asked for on demand because the overlay reads it every
 * commit and `seamMapFor` is async. It caches per body and garment, so the
 * repeat asks below cost nothing after the first.
 */
let seamMapNow: SeamMap | null = null;

/** Fetch the pairing for what is on screen, and redraw once it arrives. */
async function refreshSeamMap() {
  const want = `${bodyId}:${garment}`;
  const { seamMapFor } = await import("./seam-source");
  const map = await seamMapFor(bodyId, garment === "pants" ? "pants" : "shirt");
  // The body or garment may have moved on while this was in flight; a stale map
  // would pair a shirt's faces against a pair of pants.
  if (want !== `${bodyId}:${garment}`) return;
  seamMapNow = map;
  seamBreaks?.reset();
  // Build the index off the animation frame; see SeamBreaks.warm.
  seamBreaks?.warm();
  seamOverlay?.refresh();
  syncSeamToggle();
}
let mounting = false;

const setLabel = (text: string) => {
  if (label) label.textContent = text;
};

/**
 * What this document is: a shirt, or a pair of pants.
 *
 * Module scope because `onChange` runs per dirty rect and has to resolve part
 * groups without reaching into the autosave handle, which does not exist until
 * the end of boot. `applyGarment` is the only writer.
 */
let garment: GarmentType = "shirt";

/** The measured guide drawn under the canvas. One per garment. */
const GUIDE_SRC: Record<GarmentType, string> = {
  shirt: "/templates/Template-Shirts-R15.png",
  pants: "/templates/Template-Pants-R15.png",
  // No template exists: a t-shirt is a plain square image. Never selectable.
  tshirt: "/templates/Template-Shirts-R15.png",
};

/**
 * Every region a rect overlaps.
 *
 * The brush used to report this itself, from the region under the stroke's
 * centre. That stopped being enough once a change could be a reorder or a
 * visibility toggle, where there is no pointer and the affected area is whatever
 * the composite covers. Geometry answers it for every case; a pointer answers it
 * for one.
 */
function regionsIn(rect: DirtyRect): RegionId[] {
  const hit: RegionId[] = [];
  for (const id of Object.keys(REGIONS) as RegionId[]) {
    const r = REGIONS[id];
    if (
      rect.x < r.x + r.w &&
      rect.x + rect.w > r.x &&
      rect.y < r.y + r.h &&
      rect.y + rect.h > r.y
    ) {
      hit.push(id);
    }
  }
  return hit;
}

// ------------------------------------------------------------------- paint

/**
 * The stack owns the pixels; the composite is what everything downstream sees.
 *
 * `onChange` is the single place a change becomes visible: repaint the on-screen
 * canvas, then tell the viewer which part groups to re-upload. Every mutation —
 * a brush stroke, a reorder, an import, a visibility toggle — arrives here, so
 * none of them needs to remember to refresh anything.
 */
/** Assigned once the panel is mounted; a no-op before then. */
let renderPanel: () => void = () => {};

/** Assigned once the header is wired, and a no-op before then. */
let syncLayerActions: () => void = () => {};

/**
 * Assigned at the end of boot, and a no-op until then.
 *
 * `onChange` cannot distinguish a brush stroke from the editor assembling
 * itself — hydrate() adds layers, and every add composites. Arming this only
 * once boot is complete is what makes "the document changed" mean the user
 * changed it. See the assignment for what goes wrong otherwise.
 */
let touchSave: () => void = () => {};

/** The live autosave handle, once boot has one. */
let autosave: Awaited<ReturnType<typeof installAutosave>> | null = null;

/**
 * The garments a design can hold, and the editor can draw.
 *
 * Narrower than `GarmentType`, which also has `tshirt` — no template, no
 * regions, no edge pipeline, so nothing here could paint one.
 */
type Drawable = "shirt" | "pants";

/**
 * One stack's wiring, built per garment rather than written once.
 *
 * A design runs **two** stacks over one document, and the callbacks below are
 * all "the thing on screen changed" — repaint the canvas, re-render the panel,
 * invalidate the seam marks. Fired by the garment nobody is looking at, every
 * one of them is wrong: painting a shirt would re-render the pants' layer list.
 *
 * So the options close over which garment they belong to and return early when
 * that is not the active one. The failure this prevents is invisible until a
 * garment switch exists to expose it — the same shape as the per-garment group
 * binding warned about further down this file, and the reason the gate is
 * written now rather than when the switch lands.
 */
function stackOptionsFor(g: Drawable): LayerStackOptions {
  return {
    /*
    Every structural change a user made, recorded as one entry.

    Declared before `history` exists and reading it lazily: the stack is created
    at module scope and history needs the stack to apply an edit, so one of the
    two has to be late. This is the one that can be.

    Pushed to *this garment's* history, not the active one: an edit belongs to
    the document it changed, and reading `history` here would file a pants edit
    under the shirt during any operation that touches both.
  */
    onStructure(label, before, after) {
      if (applying) return;
      histories[g].push({ kind: "structure", label, before: [...before], after: [...after] });
    },
    onChange(rect) {
      // Everything below presents the active document. The inactive stack still
      // composites — it owns its own pixels and needs them current — but nothing
      // it does belongs on screen.
      if (g !== garment) return;
      present();
      // The stack is the only thing that owns pixels, so this is the only place
      // that can honestly say the document changed.
      touchSave();
      /*
      The handles follow the same signal as the panel.

      Selecting a different layer, undoing a drag, deleting the placement — all
      of them arrive here, and none of them are pointer events the overlay could
      have noticed for itself. Guarded by `dragging()` so a live drag is not
      redrawn twice per frame: it already redraws itself, and this fires from
      the recomposite that drag caused.
    */
      if (!transformOverlay?.dragging()) transformOverlay?.refresh();
      // Every mutation routes through here, so the panel cannot drift out of sync
      // with the stack — there is no second path that changes one without the
      // other. A full re-render of a dozen rows is cheaper than the bookkeeping
      // that would keep them selectively in step.
      renderPanel();
      // Delete has to grey out the moment the stack drops to one layer, and that
      // can happen from the row menu, the Delete key or an undo — all of which
      // arrive here and nowhere else in common.
      syncLayerActions();
      /*
      The seam marks are derived from the composite, so they are stale the
      instant it moves. Invalidated rather than recomputed: the work happens on
      the overlay's next read, so a burst of edits costs one scan rather than
      one per edit.
    */
      seamBreaks?.invalidate();
      seamOverlay?.refresh();
      // The count lives in the toggle's label, so it goes stale on exactly the
      // same signal the marks do.
      syncSeamToggle();
      if (!viewer) return;
      // A recomposite is not a stroke: reordering or hiding a layer can change any
      // face under `rect`, so the regions are read from the geometry rather than
      // from wherever a pointer happened to be.
      const groups = new Set<PartGroup>();
      for (const region of regionsIn(rect)) groups.add(groupFor(region, garment));
      if (groups.size === 0) for (const grp of groupsOf(garment)) groups.add(grp);
      for (const grp of groups) viewer.invalidate(grp, rect);
    },
  };
}

/**
 * Both garments, always. A design holds a shirt and a pair of pants from the
 * moment it exists; either may be empty, and an empty one costs an idle stack
 * and nothing else.
 */
const stacks: Record<Drawable, ReturnType<typeof createLayerStack>> = {
  shirt: createLayerStack(stackOptionsFor("shirt")),
  pants: createLayerStack(stackOptionsFor("pants")),
};

/**
 * The stack being edited — a **stable facade** over whichever garment is active.
 *
 * The obvious version of this is `let stack = stacks.shirt` reassigned by
 * `applyGarment`, and it is wrong in a way that took a browser to find. Half a
 * dozen callers take the stack *by value* at boot — `mountLayersPanel({ stack
 * })`, `mountTransformOverlay({ stack })` — and a reassignment cannot reach a
 * reference somebody already holds. The symptom: adding a layer while on pants
 * did nothing, and switching to pants listed the shirt's layers, because the
 * panel was still holding the shirt.
 *
 * So the identity never changes and the delegation does. Every existing reader
 * keeps working untouched, including any this comment has not thought of, and
 * `applyGarment` only has to move `garment`.
 *
 * The cost is one property lookup per access, against a canvas pipeline that
 * moves megabytes per stroke. Methods are bound to their real stack so `this`
 * is correct inside the closure the factory returns.
 */
const stack: ReturnType<typeof createLayerStack> = new Proxy(
  {} as ReturnType<typeof createLayerStack>,
  {
    get(_target, prop) {
      const live = stacks[garment === "pants" ? "pants" : "shirt"] as unknown as Record<
        string | symbol,
        unknown
      >;
      const value = live[prop];
      return typeof value === "function"
        ? (value as (...a: unknown[]) => unknown).bind(live)
        : value;
    },
  },
);

/** Where the brush starts. The picker owns it from the first click onward. */
const BRUSH_COLOUR = "#0074BD";

/**
 * The colour now, as opposed to the one the app started with.
 *
 * The picker pushed straight into the painter, so the live value existed only
 * inside it — fine while the brush was the only thing that painted, and wrong
 * the moment fill needed the same colour. One variable, one writer.
 */
let currentColour = BRUSH_COLOUR;

/**
 * How the next stroke combines with its layer — one function, two consumers.
 *
 * The preview (the stack's stroke overlay) and the commit (paint.ts) are
 * separate code paths. If they resolved the mode independently they could
 * disagree, and the only way to find out would be lifting the pen and watching
 * the result differ from what was on screen. So both read this.
 *
 * Declared before the painter and reading it lazily: it is only ever called
 * during a stroke, long after both exist.
 */
const tools = createToolState();

function strokeMode(): StrokeMode {
  return strokeCompositeMode({
    erasing: tools.get().active === "eraser",
    paintInsideOnly: stack.active()?.paintInsideOnly ?? false,
  });
}

/**
 * Whether the stroke in flight should be a straight line.
 *
 * Set from the pointer event, which already carries the modifier — no key
 * listener, and no chance of it disagreeing with the event that moved the
 * brush. Read by the painter per point, so holding the key partway through a
 * drag straightens what is there and releasing it hands the freehand back.
 */
let straightStroke = false;

const painter = createPainter({
  size: 24,
  colour: BRUSH_COLOUR,
  target: () => stack.activeCtx(),
  compositeMode: strokeMode,
  straight: () => straightStroke,
  onDirty(rect) {
    // The brush wrote into a layer, which is not on screen. Recompositing is
    // what makes it visible, and it fires onChange above.
    stack.markPainted();
    stack.compose(rect);
  },
  onEdit(rect, before, after) {
    const layer = stack.active();
    // No active layer means the stroke went nowhere, and a stroke that changed
    // nothing is not something anyone wants to undo.
    if (!layer || applying) return;
    history.push({
      kind: "pixels",
      label: "brush stroke",
      layerId: layer.id,
      rect,
      before,
      after,
      fabricBefore: layer.fabric,
    });
  },
  /*
    Read per stroke, not captured once: the painter is built at boot and the
    viewer is not, so there is nothing to capture yet. Returning null — no
    viewer, symmetry off — makes the mirror a no-op rather than an error.

    `mirrorMap()` caches per body and garment on the viewer's side, so asking
    on every commit costs a Map lookup.
  */
  mirror: () => (mirrorControls?.enabled() ? (viewer?.mirrorMap() ?? null) : null),
  /*
    The body under the template, so a stroke runs over a fold rather than
    stopping at a face's edge.

    Not behind a toggle. A face boundary is invisible on the body — it is a
    corner you can run a brush over — so a stroke stopping there is the
    artifact, not the default. Symmetry is a genuine choice; the shape of the
    thing being painted is not.

    Null below 1024px, where no body is loaded, and painting is template-space
    only. That is a real difference and the honest one: with no geometry there
    is nothing to say where a fold goes.
  */
  /*
    Null unless wrapping is on, which is what switches fold-crossing off.

    One gate for the painter AND the hover preview below, so the wash can never
    promise a stroke the brush will not make.
  */
  surface: () => (mirrorControls?.wrapFolds() ? (viewer?.surface() ?? null) : null),
  /*
    Whether a stroke may carry onto a DIFFERENT part, as opposed to around a
    fold within one. Off by default — see MirrorControls' JOINT_ICON note for
    the measurement behind that default.
  */
  crossParts: () => mirrorControls?.crossParts() ?? false,
  /*
    Which faces this stroke may touch. Read once per stroke by the painter, so
    changing the selection mid-drag cannot reshape a stroke already under way.
  */
  selection: () => faceSelect?.selection() ?? EMPTY,
});

/**
 * Undo and redo.
 *
 * `apply` is the only thing that puts the document back, and it is deliberately
 * the *only* writer: undo does not re-run the operation in reverse, it restores
 * the recorded state. Re-running inverted operations is how undo stacks drift
 * out of step with the document over a long session.
 *
 * Restoring must not itself be recorded, or the first undo would push an entry
 * describing the undo and the second would undo the undo. `applying` is that
 * guard.
 */
let applying = false;

/**
 * Say what just happened — seen and spoken by the same path.
 *
 * An undo in a layered document is frequently a change on a layer that is not
 * on screen, which is the whole reason `peekUndoLabel` exists. This used to be
 * an invisible live region, on the grounds that the save indicator belongs to
 * autosave and must keep meaning "is my work safe". The toaster is that missing
 * home: one call that is both visible and announced, rather than two surfaces
 * that can disagree.
 */
function announce(
  message: string,
  opts: {
    tone?: ToastTone;
    duration?: number;
    action?: { label: string; onSelect: () => void };
  } = {},
) {
  toast(message, opts);
}

/** Command or Control, decided once. */
const IS_MAC = /mac|iphone|ipad/i.test(navigator.platform || navigator.userAgent);

/** Undo or redo, and say which — the change is often on a layer off screen. */
function runHistory(direction: "undo" | "redo") {
  const edit = direction === "redo" ? history.redo() : history.undo();
  // A bare confirmation, so the short end of Material's 2-6s range.
  if (edit)
    announce(`${direction === "redo" ? "Redid" : "Undid"}: ${edit.label}`, { duration: 2500 });
}

/**
 * One history per garment.
 *
 * Undo is per garment because an edit belongs to the document it changed. The
 * alternative — one stack of edits tagged with a garment — would either undo
 * across a switch, which nobody expects, or silently skip entries that belong
 * to the other one, which is worse.
 *
 * Two consequences, both easy to get wrong and both cheap to prevent:
 * switching garment is not an edit and must not push here, and an undo must
 * never switch garment to reach an entry.
 */
function historyOptionsFor(g: Drawable): HistoryOptions {
  return {
    /*
    Keep the bar's Undo and Redo in step with the stack.

    Driven from here rather than polled, because the two are only ever wrong
    together: every push, undo, redo and trim already emits, and a button that
    says "Undo Fill face" after the entry has been trimmed for memory is worse
    than one that says nothing.

    Only the active garment's history drives the buttons; the other one is not
    what the bar is describing.
  */
    onChange({ canUndo, canRedo }) {
      if (g !== garment) return;
      syncHistoryButtons?.(canUndo, canRedo);
    },
    apply(edit: Edit, direction) {
      applying = true;
      try {
        if (edit.kind === "structure") {
          stacks[g].restore(direction === "undo" ? edit.before : edit.after);
        } else if (edit.kind === "selection") {
          /*
          Not routed through the layer stack, because a selection is not
          document state — nothing here composites, nothing is marked dirty and
          nothing reaches the file. It sets the picker and repaints the overlay
          that draws it, and that is the whole of it.

          The `applying` guard above still matters: the picker reports every
          change, and without it restoring a selection would record restoring it.
        */
          faceSelect?.set(direction === "undo" ? edit.before : edit.after);
        } else {
          const undoing = direction === "undo";
          stacks[g].putPixels(edit.layerId, edit.rect, undoing ? edit.before : edit.after);
          /*
          Put the fabric record back with the pixels it describes.

          A record carries the `rev` of the pixels it was written for, and
          putPixels has just bumped that rev — so it is re-stamped with the new
          one rather than restored verbatim. Restoring it verbatim would leave
          it pointing at a rev the layer no longer has, which is exactly the
          state that means "painted over": the colour dot would vanish on undo
          and never come back.

          `undefined` means the edit says nothing about the record, which is
          every brush stroke on every layer that was never a fabric. Only an
          edit that carries one gets to change it.
        */
          const record = undoing ? edit.fabricBefore : edit.fabricAfter;
          if (record !== undefined) {
            const layer = stacks[g].get(edit.layerId);
            if (layer) {
              restoreFabric(layer, record);
              /*
              Render again, because putPixels already did.

              It composites, which runs onChange, which renders the panel — all
              of that before the record is back. So the panel drew the layer in
              its restored-pixels-but-stale-record state, which reads as
              "painted over": the colour dot disappeared on undo and stayed
              gone, because nothing rendered again afterwards. Measured, not
              theorised: the pixels reverted and the dot did not come back.
            */
              renderPanel();
            }
          }
        }
      } finally {
        applying = false;
      }
    },
  };
}

const histories: Record<Drawable, ReturnType<typeof createHistory>> = {
  shirt: createHistory(historyOptionsFor("shirt")),
  pants: createHistory(historyOptionsFor("pants")),
};

/** The active garment's history. `applyGarment` is the only writer. */
let history: ReturnType<typeof createHistory> = histories.shirt;

/**
 * The fabric record a pixel edit has to carry so undo can put it back.
 *
 * The counterpart to the restore above, and it works because a record is never
 * deleted — it expires by `rev` instead. So at the moment an edit is recorded,
 * `layer.fabric` still holds the record describing the pixels as they were
 * *before* the write, which is exactly what undo needs. `restoreFabric`
 * re-stamps it with whatever rev the layer has by then.
 *
 * Without this, expiry is a one-way door. Undo puts the fabric's own pixels
 * back and `putPixels` bumps the rev again, so the record stays stale for good:
 * the layer *is* the fabric, no longer says so, and the colour dot never comes
 * back. Only the recolour and weave paths carried a record, so every other
 * pixel edit — a brush stroke, a fill, an in-place fabric fill, a mirror — had
 * that bug.
 *
 * `undefined` for a layer that was never a fabric, which the history reads as
 * "this edit says nothing about the record".
 *
 * There is deliberately no `recordAfter`. Redoing a paint edit must leave the
 * record stale, and saying nothing about it does that by itself.
 */
function recordBefore(layerId: string): FabricSource | undefined {
  return stack.get(layerId)?.fabric;
}

/** The handles on a placement, once the shell is wired. */
let transformOverlay: TransformOverlay | null = null;

/**
 * Soften a layer, as one undoable step.
 *
 * The original pixels are read once and held for the life of the dialog, and
 * every preview blurs THOSE — not what the last slider position left behind.
 * Blurring the canvas repeatedly as somebody drags turns it to mud inside a
 * second, and the drag is exactly when it looks like the tool is broken rather
 * than like the radius is high.
 *
 * The history entry is written on Apply and describes the whole change: the
 * previews before it never happened as far as undo is concerned.
 */
function blurLayer(layerId: string) {
  const layer = stack.get(layerId);
  const source = layer?.source;
  const ctx = source?.getContext("2d");
  if (!layer || !source || !ctx) return;

  /*
    The dialog is a live preview of the layer, so a layer that cannot be seen
    makes it a slider attached to nothing — drag it and the picture never
    changes, which reads as a broken tool rather than as a hidden layer. Same
    refusal, and the same wording, as merging one.
  */
  if (!layer.visible) {
    announce(`${layer.name} is hidden — show it first.`, { tone: "warn" });
    return;
  }
  if (layer.opacity <= 0) {
    announce(`${layer.name} is fully transparent — raise its opacity first.`, { tone: "warn" });
    return;
  }

  if (!canBlur()) {
    /*
      Say so rather than appearing to work. Assigning an unsupported
      `ctx.filter` is silent, so without this the layer would redraw untouched
      under a success toast and an undo step for nothing.
    */
    announce("This browser cannot blur. Safari supports it from version 17.", {
      tone: "warn",
      duration: 4000,
    });
    return;
  }

  /*
    A placement's pixels are its own image, which has no faces in it; an
    artwork layer's are the template, where blurring across a gutter puts ink
    on a different limb.
  */
  const zones = layer.kind === "placement" ? undefined : Object.values(REGIONS);

  const rect = { x: 0, y: 0, w: source.width, h: source.height };
  const before = ctx.getImageData(0, 0, source.width, source.height);

  const redraw = () => {
    stack.markPainted(layerId);
    stack.compose();
    transformOverlay?.refresh();
  };

  openBlurDialog({
    name: layer.name,
    isPlacement: layer.kind === "placement",
    onPreview: (radius) => {
      // Always from `before`, so the preview shows the radius rather than the
      // sum of every radius the slider has passed through.
      blurInto(source, before, radius, { zones });
      redraw();
    },
    onCancel: () => {
      restorePixels(source, before);
      redraw();
    },
    onApply: (radius) => {
      blurInto(source, before, radius, { zones });
      stack.markPainted(layerId);
      const after = ctx.getImageData(0, 0, source.width, source.height);
      history.push({
        kind: "pixels",
        label: `Blur ${radius} px`,
        layerId,
        rect,
        before,
        after,
        /*
          `fabricBefore` alone, and no `fabricAfter`.

          A blur is exactly what "painted over" means for a fabric fill: the
          flat colour is gone and re-filling it would destroy the softening.
          markPainted has already bumped the rev the record is stamped with,
          which expires it — so redo needs to say nothing, and `undefined` is
          how an edit says nothing. Undo still puts the record back.
        */
        fabricBefore: recordBefore(layerId),
      });
      /*
        Redo does not come back bit-identical here, and that is canvas, not us.

        ImageData is unpremultiplied while the canvas stores premultiplied, so
        `getImageData` -> `putImageData` rounds any pixel that is partly
        transparent. A stroke is alpha 0 or 255 almost everywhere and survives
        exactly, which is why undo is exact; a blur is nothing but partial
        alpha. Measured at up to 3/255 on ~7% of channels — invisible, and the
        alternative is keeping a second canvas per undo step.
      */
      stack.compose();
      transformOverlay?.refresh();
      announce(`${layer.name} blurred ${radius} px`, { tone: "success", duration: 1800 });
    },
  });
}

/**
 * Export one file per badge, as a single zip.
 *
 * The rank set, and the reason someone pays for this. Doing it by hand is: hide
 * seven layers, export, hide a different seven, export — eight times, with no
 * way afterwards to tell which file is which except by opening them.
 *
 * The badges are the placement layers. That is not a guess about intent: a
 * placement is an image put on a face, which is exactly what a rank badge is,
 * and the artwork layers underneath are the shirt every rank shares.
 *
 * Visibility is driven through `stack.setVisible`, which does not record
 * history — so eight exports do not leave sixteen entries on the undo stack.
 * The base is restored in a `finally`, because an export that throws halfway
 * must not leave the document showing one badge and hiding the rest.
 */
async function exportRankSet(title: string, garment: GarmentType, bodyId?: string) {
  const badges = stack
    .layers()
    .filter((l) => l.kind === "placement")
    .map((l) => ({ id: l.id, name: l.name }));

  if (!canMakeRankSet(badges)) {
    announce("A rank set needs at least two image layers — one badge per rank.", {
      tone: "info",
      duration: 3000,
    });
    return;
  }

  const { download, filenameFor, prepareExport } = await import("./export");
  const { zipSync } = await import("fflate");
  const steps = planRankSet(badges, filenameFor(title, garment));
  // Captured before anything is hidden, so the restore below is to the
  // document as the user left it rather than to whatever the loop last set.
  const was = new Map(stack.layers().map((l) => [l.id, l.visible]));
  const files: Record<string, Uint8Array> = {};

  announce(`Exporting ${steps.length} files…`, { tone: "info", duration: 1500 });
  try {
    for (const step of steps) {
      for (const [id, over] of Object.entries(step.overrides)) {
        if (over.visible !== undefined) stack.setVisible(id, over.visible);
      }
      const prepared = await prepareExport(stack.canvas, { title, garment, body: bodyId });
      files[step.filename] = new Uint8Array(await prepared.blob.arrayBuffer());
      prepared.dispose();
    }
  } finally {
    for (const [id, visible] of was) stack.setVisible(id, visible);
  }

  /*
    Stored, not deflated. Every entry is a PNG, which is already deflate — so
    compressing again spends CPU on eight images to save approximately nothing.
  */
  const zip = zipSync(files, { level: 0 });
  download(
    new Blob([zip], { type: "application/zip" }),
    rankSetZipName(filenameFor(title, garment)),
  );
  countUsage("export_rank_set");
  announce(`${steps.length} files exported`, { tone: "success", duration: 2600 });
}

/**
 * Name the undo step after what the gesture actually did.
 *
 * "Undo transform" tells you nothing at the moment you are trying to decide
 * whether to press it. Which of the three changed is already known from the
 * two placements, so the label costs a comparison rather than threading the
 * handle through the commit.
 */
const TRANSFORM_LABEL = {
  move: "Move image",
  scale: "Resize image",
  rotate: "Rotate image",
} as const;

function transformKind(before: FacePlacement, after: FacePlacement): keyof typeof TRANSFORM_LABEL {
  if (before.scale !== after.scale) return "scale";
  if (before.rotation !== after.rotation) return "rotate";
  return "move";
}

/**
 * The template point at the middle of what is currently on screen.
 *
 * Where a placement goes when it did not arrive by pointer — the file picker
 * and paste have no coordinates of their own, and "the middle of the canvas" is
 * wrong once the view can be panned: at 8x on a sleeve, the chest is the last
 * place anybody wants their logo.
 *
 * Uses the same ratio as `toTemplate`, against the viewport rather than the
 * pointer, so it is correct at any zoom for the same reason that is.
 */
function templatePointAtViewCentre(): { x: number; y: number } {
  const el = paintCanvas;
  if (!el) return { x: CANVAS.width / 2, y: CANVAS.height / 2 };
  const canvas = el.getBoundingClientRect();
  const port = el.closest(".rcm-viewport")?.getBoundingClientRect() ?? canvas;
  if (!(canvas.width > 0)) return { x: CANVAS.width / 2, y: CANVAS.height / 2 };
  const cx = port.left + port.width / 2;
  const cy = port.top + port.height / 2;
  return {
    x: ((cx - canvas.left) / canvas.width) * CANVAS.width,
    y: ((cy - canvas.top) / canvas.height) * CANVAS.height,
  };
}

/**
 * Put an image on a face, as one undoable step.
 *
 * `nearestFace` rather than `regionAt`, so a centre that has landed in the dead
 * space between faces still places somewhere sensible instead of refusing. The
 * dead space is 54.9% of this canvas, so "the middle of the view is not on a
 * face" is the common case rather than the odd one.
 */
async function placeImageFile(file: File) {
  if (!isPlaceableImage(file)) {
    announce("That file is not an image this can place.", { tone: "warn" });
    return;
  }
  try {
    const decoded = await decodeImage(file);
    const centre = templatePointAtViewCentre();
    const face = nearestFace(centre.x, centre.y);
    const name = layerNameFor(file.name);
    const placement = initialPlacement(face, decoded.aspect);
    stack.transaction(`Place ${name}`, () =>
      stack.addPlacement({
        name,
        image: decoded.image,
        width: decoded.width,
        height: decoded.height,
        placement,
      }),
    );
    announce(`${name} placed on ${faceLabel(face, garmentForLabel())}`, {
      tone: "success",
      duration: 2200,
    });
  } catch {
    announce("That image could not be read.", { tone: "warn" });
  }
}

/**
 * "Add image", beside Fabrics.
 *
 * Beside Fabrics because they are the same job — putting something on the
 * garment that you did not draw — and deliberately NOT on the drop target,
 * which means "import a template" and is the product's front door. One gesture
 * cannot mean both without guessing, and the obvious tell (is it 585x559?) is
 * wrong for anyone who exports at 2x.
 */
/**
 * Open a file dialog and hand back what was chosen.
 *
 * Built and discarded per use rather than kept hidden in the DOM. A persistent
 * input needs its value cleared after every pick — choosing the same file twice
 * fires no `change` event and the second attempt looks like a dead button — and
 * a fresh element cannot have a stale value to begin with.
 */
function chooseImage(onPick: (file: File) => void) {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = PLACEABLE_TYPES.join(",");
  input.hidden = true;
  input.addEventListener("change", () => {
    const file = input.files?.[0];
    if (file) onPick(file);
    input.remove();
  });
  document.body.appendChild(input);
  input.click();
}

/**
 * Swap a placement's pixels, keeping where it sits.
 *
 * The transform survives, which is the whole reason this exists rather than
 * "delete it and place another": a badge positioned and rotated onto a sleeve
 * should not have to be positioned and rotated again because the artwork behind
 * it changed.
 *
 * The scale is re-fitted only if it has to be. `scaleToFit` returns the scale
 * unchanged when the new image still fits, so a same-shaped replacement keeps
 * exactly the size that was chosen — but swapping a wide banner for a tall
 * crest would otherwise leave it hanging off its own face at a size nobody
 * picked.
 */
async function replaceImageFor(layerId: string, file: File) {
  const layer = stack.get(layerId);
  if (!layer?.placement) return;
  if (!isPlaceableImage(file)) {
    announce("That file is not an image this can place.", { tone: "warn" });
    return;
  }
  try {
    const decoded = await decodeImage(file);
    const placement = layer.placement;
    const fitted = scaleToFit(placement, decoded.aspect);
    stack.transaction("Replace image", () => {
      stack.setPixels(layerId, decoded.image, decoded.width, decoded.height);
      if (fitted !== placement.scale) stack.setPlacement(layerId, { scale: fitted });
    });
    transformOverlay?.refresh();
    announce(`${layer.name} replaced`, { tone: "success", duration: 1800 });
  } catch {
    announce("That image could not be read.", { tone: "warn" });
  }
}

function mountPlaceImage(rail: HTMLElement | null) {
  if (!rail) return;

  const button = document.createElement("button");
  button.type = "button";
  button.className = "icon-btn";
  button.setAttribute("aria-label", "Add an image");
  button.title = "Put an image on a face — a logo, a badge, a patch";
  button.innerHTML = `<svg class="icon" aria-hidden="true" focusable="false"><use href="#i-import" /></svg>`;
  button.addEventListener("click", () => chooseImage((file) => void placeImageFile(file)));

  const fabrics = rail.querySelector("[data-fabrics]");
  rail.insertBefore(button, fabrics?.nextSibling ?? null);

  /*
    Paste, because that is how a logo actually arrives.

    Copied out of Figma, Procreate or a browser tab, and the alternative is
    saving it to disk first purely to hand it back through a file picker.
  */
  document.addEventListener("paste", (e) => {
    const items = [...(e.clipboardData?.items ?? [])];
    const item = items.find((i) => i.kind === "file" && isPlaceableImage(i));
    if (!item) return;
    const file = item.getAsFile();
    if (!file) return;
    e.preventDefault();
    void placeImageFile(file);
  });
}

/**
 * The Move tool and the zoom readout, at the foot of the rail.
 *
 * Two controls rather than the usual four. Zooming itself is already reachable
 * from a wheel, a pinch and `+`/`-`, so in and out buttons would be a third
 * spelling of something nobody is short of ways to do. What zoom actually needs
 * in the HUD is the thing none of those provide: somewhere to read what the
 * scale currently *is*, and a way back when you are lost at 30x with no idea
 * which sleeve you are looking at. The readout is both — it shows the percent
 * and fits the whole template when pressed.
 *
 * Built here rather than in the shell generator because the rail's markup is a
 * file the other session has open, and because the readout has to be written to
 * on every view change anyway.
 */
function mountViewControls(rail: HTMLElement | null) {
  if (!rail || !viewControl) return;

  const sep = document.createElement("hr");

  const move = document.createElement("button");
  move.type = "button";
  move.className = "icon-btn";
  move.dataset.tool = "pan";
  move.setAttribute("aria-pressed", "false");
  move.setAttribute("aria-label", "Move the canvas");
  move.title = "Move the canvas (H) — or hold space, or drag with two fingers";
  move.innerHTML = `<svg class="icon" aria-hidden="true" focusable="false"><use href="#i-pan" /></svg>`;

  const zoom = document.createElement("button");
  zoom.type = "button";
  zoom.className = "icon-btn rcm-zoom";
  zoom.style.cssText = "font-size:11px; font-variant-numeric:tabular-nums; font-weight:600;";
  zoom.addEventListener("click", () => viewControl?.fit());

  /*
    The seam toggle sits with pan and zoom because it is a view control: it
    changes how the template is looked at, not what is in it. Not in the mirror
    group, which changes strokes, and not a mode chip, which reports a mode that
    changes what a stroke does.
  */
  const seams = document.createElement("button");
  seams.type = "button";
  seams.className = "icon-btn rcm-seams";
  seams.innerHTML = `<svg class="icon" aria-hidden="true" focusable="false"><use href="#i-edge-check" /></svg>`;
  /*
    The words, from the state, in one place.

    Two paths reach this control — the click, and every recompute of the marks —
    and `mirror-controls.ts` paid for the version where the click handler wrote
    them: the shortcut path flipped `aria-pressed` and left the tooltip
    describing the state the control had just left.

    The count is in the label because the overlay itself is `aria-hidden`. The
    marks are a picture, and a picture of a defect is worth nothing to somebody
    reading the page rather than looking at it. This is the only place the
    finding exists in words while you are still drawing.
  */
  const saySeams = () => {
    const breaks = seamBreaks?.current();
    let label: string;
    if (!seamMapNow) {
      label = "Seam check needs a body — the folds are read off its geometry";
    } else if (!seamMarksOn) {
      label = "Show folds that do not line up";
    } else if (!breaks || breaks.breaks.length === 0) {
      label = "Seam check on — every fold lines up. Click to hide";
    } else {
      const n = breaks.breaks.length;
      label = `Seam check on — ${n} ${n === 1 ? "fold does" : "folds do"} not line up. Click to hide`;
    }
    seams.setAttribute("aria-pressed", String(seamMarksOn && !!seamMapNow));
    seams.setAttribute("aria-label", label);
    seams.title = label;
  };
  seams.addEventListener("click", () => {
    /*
      Refuses without a body and says why, like the four geometry controls.

      Toggling anyway would switch a mode that cannot draw anything, which is
      indistinguishable from the feature being broken — and this is the state a
      new document is in for the first second of its life.
    */
    if (!seamMapNow) {
      saySeams();
      return;
    }
    seamMarksOn = !seamMarksOn;
    saySeams();
    seamOverlay?.refresh();
  });
  saySeams();
  syncSeamToggle = saySeams;

  rail.appendChild(sep);
  rail.appendChild(move);
  rail.appendChild(zoom);
  rail.appendChild(seams);

  const show = (scale: number) => {
    const pct = Math.round(scale * 100);
    zoom.textContent = `${pct}%`;
    const label = `Zoom ${pct}%. Fit the whole template (0)`;
    zoom.setAttribute("aria-label", label);
    zoom.title = label;
  };
  show(viewControl.get().scale);
  viewZoomReadout = show;
}

/** Set by `mountViewControls`; called whenever the view changes. */
let viewZoomReadout: ((scale: number) => void) | null = null;

/**
 * Re-render the seam toggle's words. Set by `mountViewControls`.
 *
 * A no-op before the rail exists, which is the same shape `viewZoomReadout`
 * uses above and for the same reason: boot order should not be a caller's
 * problem.
 */
let syncSeamToggle: () => void = () => {};

/**
 * Set by `mountHistoryButtons`, called by the history's own `onChange`.
 *
 * Null until the shell is wired, because the history is created at module scope
 * and the bar is not — an edit recorded before the buttons exist must not throw.
 */
let syncHistoryButtons: ((canUndo: boolean, canRedo: boolean) => void) | null = null;

/**
 * Undo and Redo, as controls rather than as keystrokes.
 *
 * The shell carried a note where these used to sit: they "were here and did
 * nothing", so they were removed rather than left lying — the right call at the
 * time. The stack is real now, and their absence had become the more expensive
 * half of that trade.
 *
 * **The reason this matters more than it looks.** Undo was reachable only by
 * ⌘Z. A large share of the people who make Roblox clothing work on an iPad,
 * where there is no keyboard at all — so the single most-reached-for control in
 * any editor was not merely inconvenient, it was unreachable. Everything else
 * in the toolbar is a thing you might want; undo is the thing you need before
 * you will risk trying any of them.
 *
 * Built here rather than in the shell generator because the label has to name
 * the edit — "Undo Fill face" — and only the live stack knows what that is.
 */
function mountHistoryButtons() {
  const bar = document.querySelector<HTMLElement>(".bar-actions");
  if (!bar) return;

  const make = (act: "undo" | "redo") => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "icon-btn";
    button.dataset.history = act;
    /*
      The same `<use>` the generator emits. The sprite already carries both
      symbols — they were registered in icons.json and never rendered — so this
      is the identical markup rather than a second copy of the geometry.
    */
    button.innerHTML = `<svg class="icon" aria-hidden="true" focusable="false"><use href="#i-${act}" /></svg>`;
    button.addEventListener("click", () => void runHistory(act));
    return button;
  };

  const undoButton = make("undo");
  const redoButton = make("redo");
  /*
    Ahead of the existing separator, so the bar reads: history, then the
    import/export pair it already had.

    Inserted one at a time and in reverse, because @cloudflare/workers-types
    shadows the DOM's variadic ParentNode methods in this project and the
    two-argument form does not typecheck — the same trap panel.ts documents
    for `append`.
  */
  bar.insertBefore(redoButton, bar.firstChild);
  bar.insertBefore(undoButton, bar.firstChild);

  syncHistoryButtons = (canUndo, canRedo) => {
    /*
      Disabled *and* named. A greyed button with no explanation is a dead end;
      naming the edit turns "can I take that back?" into a question the toolbar
      answers without being clicked — which is the whole reason to spend bar
      width on this rather than leave it to a keystroke.
    */
    const set = (button: HTMLButtonElement, can: boolean, verb: string, what: string | null) => {
      button.disabled = !can;
      const label = can && what ? `${verb} ${what}` : `Nothing to ${verb.toLowerCase()}`;
      button.setAttribute("aria-label", label);
      button.title = label;
    };
    set(undoButton, canUndo, "Undo", history.peekUndoLabel());
    set(redoButton, canRedo, "Redo", history.peekRedoLabel());
  };

  // Nothing has happened yet, so both start disabled and say so.
  syncHistoryButtons(false, false);
}

/**
 * Bring back a saved version, as one undoable edit.
 *
 * The whole point, and the thing that is easy to get wrong: this is an **edit**,
 * not a reset. Replacing the stack outside `history` would mean somebody
 * restores the wrong version and their current work is simply gone, with no way
 * back — the most destructive button in the product, wearing the friendliest
 * word.
 *
 * `StructureEdit` already carries the entire layer list as `before` and `after`
 * rather than a diff, so a whole-document swap fits it exactly. Undo puts the
 * previous document back in one step.
 *
 * The new layers are built in a throwaway stack first. `RuntimeLayer`s are plain
 * objects holding canvases and are not bound to the stack that made them, so
 * hydrating elsewhere and swapping the result in means the live document is
 * never in a half-restored state — and a version that fails to open leaves the
 * editor exactly as it was.
 *
 * A restore is also a *save*: the recovered contents become a new version and
 * the revision only ever moves forward. Rewinding it would fight the
 * `where revision = ?` guard in `saveVersion` and lose.
 */
export async function restoreVersion(project: Project, blobs: Record<string, Uint8Array>) {
  const { createLayerStack } = await import("./layers");
  const scratch = createLayerStack({});
  const info = await hydrate(scratch, project, blobs);
  const after = [...scratch.layers()];

  // Nothing came back. Wiping the document to show an empty restore would be
  // the one outcome worse than refusing.
  if (after.length === 0 && info.restored === 0) {
    throw new Error("that version could not be opened");
  }

  const before = [...stack.layers()];
  applying = true;
  try {
    stack.restore(after);
  } finally {
    applying = false;
  }
  history.push({ kind: "structure", label: "restore version", before, after });

  // The document changed, so it saves — as a new version, at the next revision.
  touchSave();
  return info;
}

/**
 * Client coordinates to template pixels.
 *
 * The canvas is letterboxed by CSS, so its bounding rect IS the drawn area and
 * the mapping is a straight ratio — no offset maths, which is where this
 * normally goes wrong.
 */
function toTemplate(e: PointerEvent, el: HTMLCanvasElement) {
  const r = el.getBoundingClientRect();
  return {
    x: ((e.clientX - r.left) / r.width) * el.width,
    y: ((e.clientY - r.top) / r.height) * el.height,
    // A mouse reports 0.5; a pen reports real pressure. Treat 0 as "no sensor"
    // rather than "pressed as lightly as possible", or mice paint nothing.
    pressure: e.pressure > 0 ? e.pressure : 0.5,
    // Shift straightens the stroke. Taken from the same event as the position,
    // so the two can never describe different moments.
    straight: e.shiftKey,
  };
}

function wirePainting(el: HTMLCanvasElement) {
  // Our own flag rather than hasPointerCapture as the gate. Capture is a
  // nice-to-have — it keeps a stroke alive when the pointer leaves the canvas —
  // but if it ever fails, gating on it means painting silently stops working
  // with no error. The stroke state is ours; capture is an enhancement.
  let painting = false;

  el.addEventListener("pointerdown", (e) => {
    /*
      Anything but the primary button is not a stroke.

      Nothing checked this, so a right-click or a middle-click on the canvas
      began one — a context menu that also leaves a dab, and a middle-drag that
      pans and paints at once.
    */
    if (e.button !== 0) return;
    /*
      The pan gesture owns this press.

      The view controller listens on the viewport, a DIV wrapping the canvas, so
      a pointerdown reaches the canvas FIRST and its `preventDefault` does not
      stop this listener. Holding space or choosing the Move tool therefore
      started a stroke and panned at the same time. Asking the controller keeps
      one definition of what a pan is, rather than a copy here that drifts the
      day a fourth way to grab the canvas is added.
    */
    if (viewControl?.wantsPan(e)) return;
    /*
      A handle on the selected placement, which is not a stroke.

      Offered rather than intercepted: the overlay is `pointer-events: none` and
      hands the press back here, so nothing races this listener and the view
      controller still sees the event — which is what lets a pinch that begins
      on a placement be recognised as a pinch.
    */
    if (transformOverlay?.tryBegin(e)) return;
    /*
      The face picker takes what is left.

      This used to be the FIRST guard, and that was the bug: with a placement
      selected its handles were drawn and completely dead, because the picker
      swallowed the press before the transform was ever asked. Reported as "the
      face picker was stuck and I couldn't use transform", which is exactly what
      it was.

      It sits here rather than first because face selection is a scope for
      PAINTING, and a placement cannot be painted on at all — so it has nothing
      to say about one. A press that misses the handles still reaches it, so
      picking faces is unchanged whenever there is nothing selected to grab.
    */
    if (tools.get().active === "faces") return;
    /*
      Fill is a tap, not a stroke.

      It commits in one go, so it never touches the wet layer or the painter —
      it writes pixels and pushes one history entry, the same shape a re-fill
      of a fabric layer uses.
    */
    /*
      The eyedropper samples the artwork, never the screen.

      There is a native EyeDropper API and it is the wrong tool here: it is
      Chromium-only, and it samples *the screen*, which under this canvas
      includes the Roblox template guide drawn beneath the artwork at 35%. It
      would return artwork-blended-with-guide — a colour that is not in the file
      and cannot be matched again.
    */
    if (tools.get().active === "eyedropper") {
      const at = toTemplate(e, el);
      sampleColour(at.x, at.y);
      return;
    }
    if (tools.get().active === "fill") {
      const at = toTemplate(e, el);
      applyFill(at.x, at.y);
      return;
    }
    /*
      Refuse the stroke and say why, rather than swallowing it.

      `paintBlockedReason()` has known the answer since the layer stack was
      written and had no caller until now. The hidden-layer case is the cruel
      one: the paint really is being written, it simply cannot be seen, which
      from the outside is indistinguishable from a broken brush. Photoshop
      refuses and explains; doing neither is the worst of the three options.
    */
    const blocked = stack.paintBlockedReason();
    if (blocked) {
      announce(blocked, { tone: "warn" });
      return;
    }

    try {
      el.setPointerCapture(e.pointerId);
    } catch {
      // Non-fatal: the stroke just ends if the pointer leaves the element.
    }
    painting = true;
    /*
     * Drop the 3D preview's resolution for the duration of the stroke.
     *
     * The avatar re-renders every frame while painting, and its drawing buffer
     * is sized to the pane — 2.47 MP on a 1920x1080 display at 2x, 6.44 MP on a
     * 3440x1440 one. Measured, that was 26 ms a frame with 48% of frames over
     * budget; the same scene at reduced scale is about a third of the fill
     * work. `finish()` restores it, so the picture you actually stop and look
     * at is always full resolution.
     */
    viewer?.setInteractive(true);
    // The stroke is about to say this better than the preview can.
    footprint?.hide();
    const p = toTemplate(e, el);
    straightStroke = p.straight;
    painter.begin(p.x, p.y, p.pressure);
  });

  el.addEventListener("pointermove", (e) => {
    if (!painting) {
      /*
        Preview where the brush would land, before it lands.

        Only while hovering: during a stroke the paint itself is the answer, and
        a wash under it would fight what it is describing. The overlay throttles
        itself by distance, so this is far cheaper than it looks.
      */
      const at = toTemplate(e, el);
      footprint?.show(at.x, at.y);
      return;
    }
    // Coalesced events give every sample the OS captured between frames, which
    // is the difference between a smooth stroke and a polyline on a fast drag.
    const events = e.getCoalescedEvents?.() ?? [e];
    for (const ev of events) {
      const p = toTemplate(ev, el);
      straightStroke = p.straight;
      painter.extend(p.x, p.y, p.pressure);
    }
  });

  const finish = (e?: PointerEvent) => {
    if (!painting) return;
    painting = false;
    // Back to full resolution. This runs from pointerup, pointercancel,
    // lostpointercapture and window blur alike — every way a stroke can end
    // already funnels through here, which is why the restore belongs here and
    // not beside the pointerup listener.
    viewer?.setInteractive(false);
    try {
      if (e && el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
    } catch {
      // Already released.
    }
    painter.end();
  };

  /*
    Ending a stroke listens on the WINDOW, not the canvas.

    It used to listen on the canvas only, on the reasoning that pointer capture
    keeps the pointer's events coming back to the element. That holds right up
    until capture is not granted or is lost — and then a release outside the
    canvas fires `pointerup` somewhere else entirely, `painting` stays true, and
    the next pointer move silently continues the stroke. Which is exactly what
    it looked like: paint kept appearing after the press was lifted.

    The second symptom was worse and had the same cause. A stroke that never
    ends is never committed, so it lives only on the wet layer — and the next
    `begin()` clears the wet layer to start fresh. Earlier lines did not "vanish"
    so much as never reach the artwork in the first place.

    `lostpointercapture` is the canonical signal that capture went away, and it
    fires in cases no pointerup ever will.
  */
  window.addEventListener("pointerup", finish);
  window.addEventListener("pointercancel", finish);
  el.addEventListener("lostpointercapture", () => finish());
  // Alt-tabbing mid-stroke is the other way to leave one open forever.
  window.addEventListener("blur", () => finish());
}

/**
 * Mirror the paint surface onto the visible canvas, on demand.
 *
 * The first version ran a permanent requestAnimationFrame loop. Two problems:
 * it re-copied a 585x559 buffer sixty times a second whether or not anything
 * changed — 1.3 MB of churn per frame, the exact waste the viewer contract
 * exists to avoid — and it drew nothing at all whenever the browser was not
 * compositing, so the canvas could sit blank with no error.
 *
 * Painting is event-driven, so presenting should be too.
 */
let present: () => void = () => {};

/**
 * The template image is a GUIDE, not artwork.
 *
 * It was briefly the paint surface's starting content, which is wrong in a way
 * that only shows up at export: the exported PNG then contains the UP/FRONT/BACK
 * labels, the Roblox wordmark and the instruction text, and someone uploads that
 * as their shirt. It also made the canvas fully opaque, so dilation had nothing
 * to bleed into and reported 0 px — the export silently did nothing.
 *
 * So the guide is drawn underneath, faintly, and never leaves this function.
 * The paint surface starts empty and is the only thing exported.
 */
let guide: HTMLImageElement | null = null;

function bindPresenter(el: HTMLCanvasElement) {
  const ctx = el.getContext("2d");
  if (!ctx) return;
  present = () => {
    ctx.clearRect(0, 0, el.width, el.height);
    if (guide) {
      ctx.globalAlpha = 0.35;
      ctx.drawImage(guide, 0, 0, el.width, el.height);
      ctx.globalAlpha = 1;
    }
    ctx.drawImage(stack.canvas, 0, 0);
  };
  present();
}

/**
 * The template guide, loaded in a way that survives a background tab.
 *
 * This was `await img.decode()`, and boot awaits it before anything else runs.
 * `decode()` does not settle while the document is hidden in Chrome, so an
 * editor opened in a background tab — a cmd-clicked link, a restored session,
 * any second tab — hung on this line forever and presented a permanently blank
 * canvas with no error, because nothing had thrown.
 *
 * `load`/`error` do fire while hidden, so they are the gate.
 *
 * `decode()` is still called, but **never awaited** — and the distinction is
 * the whole fix. Its promise does not reject when the document is hidden; it
 * simply never settles, so `await img.decode().catch(...)` blocks exactly as
 * hard as the original did. A `catch` cannot rescue a promise that never
 * resolves. Kicking it off unawaited keeps the benefit it was there for — no
 * decode hitch on the first draw of a visible page — and costs nothing when
 * that promise is abandoned. `load` has already guaranteed the bytes are
 * drawable, which is all boot actually needs.
 */
async function loadGuide(src: string) {
  const img = new Image();
  const ready = new Promise<void>((resolve, reject) => {
    img.addEventListener("load", () => resolve(), { once: true });
    img.addEventListener("error", () => reject(new Error(`could not load ${src}`)), { once: true });
  });
  img.src = src;
  await ready;
  void img.decode().catch(() => {});
  guide = img;
}

/**
 * Point the groups this garment covers at the composite, and release the rest.
 *
 * `setGarment` swaps the ambient-occlusion map and nothing else, so a group left
 * bound keeps its last uploaded texture indefinitely — which is how a pair of
 * pants ends up with the arms still wearing a shirt. Releasing is what makes an
 * uncovered limb bare skin again.
 */
function bindSurfaces() {
  if (!viewer) return;
  const covered = new Set<PartGroup>(groupsOf(garment));
  for (const group of ["torso", "arms", "legs"] as PartGroup[]) {
    // The composite itself, the same buffer layers, undo and export all read.
    // The seam preview used to substitute a bled copy here and nowhere else;
    // it is gone, so there is one surface again.
    viewer.setSurface(group, covered.has(group) ? stack.canvas : null);
  }
}

/**
 * Switch what this document is, everywhere at once.
 *
 * The only writer of `garment`, and it has four jobs because a garment is not a
 * label — it changes the picture under the canvas, what the 3D preview paints,
 * and what the saved file claims to be. Doing any one of those without the
 * others produces a document that disagrees with itself: pants artwork under a
 * shirt guide, or a file that uploads onto the wrong limbs.
 *
 * `persist` is false while boot is applying what was restored — that is not a
 * change, and writing it back would mark a freshly opened document dirty.
 */
async function applyGarment(next: GarmentType, persist: boolean) {
  garment = next;
  /*
   * Point at the other document.
   *
   * Before the outfit work this line did not exist, because a garment was a
   * label on one stack rather than a stack of its own. `tshirt` has no stack —
   * no template, no regions — so it leaves the current one in place rather than
   * indexing to undefined.
   */
  if (next === "shirt" || next === "pants") {
    // `stack` is a facade and follows `garment` on its own; only history is a
    // plain binding, because nothing takes it by value.
    history = histories[next];
    // The panel, the layer buttons and the undo buttons are all describing the
    // other garment now. None of them is driven by a stack event, because
    // nothing about the *stacks* changed — only which one is being looked at.
    renderPanel();
    syncLayerActions();
    syncHistoryButtons?.(history.state.canUndo, history.state.canRedo);
  }
  // The pairing is per garment: the same rectangles wrap arms or legs, and
  // which faces meet differs accordingly.
  void refreshSeamMap();

  await loadGuide(GUIDE_SRC[next]).catch(() => {
    // A missing guide is a faint picture under the canvas, not a reason to stop
    // switching. Painting still works; the tracing aid does not.
  });
  present();

  if (viewer) {
    viewer.setGarment(next === "pants" ? "pants" : "shirt");
    // Rebind before invalidating: the group this garment does not cover has to
    // let go of the canvas, or it keeps showing the last thing uploaded to it.
    bindSurfaces();
    // Every pixel means something different now, so the dirty rect is the whole
    // canvas and both groups — this is the one change that is never localised.
    for (const g of groupsOf(next)) viewer.invalidate(g);
  }

  if (persist) autosave?.setGarment(next);
}

/**
 * The rail.
 *
 * Buttons carry their ToolId in `data-tool`, so there is no second mapping from
 * button to tool to keep in step with the registry. Unimplemented tools are not
 * rendered at all — `select()` would refuse them anyway, but a button that
 * refuses is still a button that did nothing.
 */
/**
 * The tool options panel, once it is mounted.
 *
 * Declared ahead of the rail wiring because the click handler closes over it
 * and the panel is built further down the same function — the handler only runs
 * after a user click, by which time it is assigned.
 */
let toolOptions: ReturnType<typeof mountToolOptions> | null = null;
/** The faces tool's options: the accessible path to the canvas picker. */
let faceList: ReturnType<typeof mountFaceList> | null = null;

/** The where-will-this-land wash under the pointer. Null below 1024px. */
let footprint: ReturnType<typeof mountFootprintOverlay> | null = null;
/** Pan and zoom. Null until the shell is wired. */
let viewControl: ViewController | null = null;

function wireTools() {
  /*
    Pan and zoom go in before anything that sits on the canvas.

    The overlays below find their stage through `canvas.parentElement`, so
    installing the view first is what puts them inside the transformed wrapper
    and keeps them lined up with the artwork texel for texel — with no changes
    to either of them. The mode chips mounted earlier, at module scope, and
    stay outside it, which is what stops them sliding off the pane at 8x.
  */
  if (paintCanvas) {
    viewControl = mountViewController({
      canvas: paintCanvas,
      content: { w: CANVAS.width, h: CANVAS.height },
      // A plain drag pans only while the Move tool is up. Space, the middle
      // button and two fingers pan regardless — see view-controller.ts.
      panning: () => tools.get().active === "pan",
      // A second finger turns a stroke into a pinch. Drop the stroke rather
      // than commit it, or every zoom leaves a smear.
      onGestureTakeover: () => {
        painter.cancel();
        // A half-finished drag committed on the way into a zoom is the same
        // smear a half-finished stroke would be.
        transformOverlay?.cancel();
      },
      onChange: (v) => {
        viewZoomReadout?.(v.scale);
        // Handle sizes divide by the zoom, so a zoom change resizes them.
        transformOverlay?.refresh();
      },
    });
  }

  /*
    The rail is looked up first because the face picker mounts INTO it, and has
    to exist before the `[data-tool]` query below binds the rail's buttons.
  */
  const rail = document.querySelector<HTMLElement>(".rail");

  /*
    The face picker and the overlay it draws on.

    At boot rather than with the viewer, because neither needs geometry: a
    selection is a set of rectangles on the template, and it should work at any
    width. The symmetry guide shares the overlay and simply draws nothing until
    a body gives it a map.
  */
  if (rail && paintCanvas) {
    const rule = document.createElement("hr");
    const group = document.createElement("div");
    group.className = "mirror-group";
    rail.appendChild(rule);
    rail.appendChild(group);
    faceSelect = mountFaceSelect({
      mount: group,
      canvas: paintCanvas,
      /*
        The rail owns which tool is active; the picker only asks — and it also
        has to be paintable.

        The picker registers its own pointerdown on the canvas and mounts
        before `wirePainting`, so its listener runs FIRST: guard order in the
        painter cannot hold it back. With an image selected it therefore
        swallowed every press while the transform handles sat there looking
        operable, which is what "the face picker was stuck and I couldn't use
        transform" was.

        Gated here instead, and semantically rather than by precedence: a face
        selection is a scope for PAINTING, and a placement cannot be painted on
        — so there is nothing for the picker to scope and it stands down.
      */
      active: () => tools.get().active === "faces" && stack.active()?.kind !== "placement",
      // Escape clears the selection here and puts the tool down there, because
      // a selection tool you cannot escape from is the one everybody complains
      // about — and only the shell knows what to fall back to.
      onEscape: () => tools.select("brush"),
      onChange: (sel) => {
        symmetryOverlay?.refresh();
        modeChips?.set(
          { ...FACES_CHIP, label: describeSelection(sel, garmentForLabel()) },
          sel.size > 0,
        );
        /*
          Fade whatever stands in front of the faces you picked.

          Six of eighteen shirt faces are behind something and eight of pants
          are, so choosing one and looking at the body shows nothing happening.
          Painting a sleeve's inner face meant orbiting and squinting; now the
          arm and torso in front of it drop to a silhouette and stay there while
          you work, because the selection persists and this is driven by it.

          Empty selection puts the body back together, which is the rule the
          plan gives — and it falls out of passing the selection straight
          through, with no branch here to get wrong.
        */
        ghost?.revealAll([...sel]);
        // The layer rows show a "limit to these faces" control only while
        // there is a selection to apply, so they have to hear about it.
        renderPanel?.();
      },
      /*
        One gesture, one undo step.

        `applying` is the same guard the stack's own edits use: restoring a
        selection sets the picker, the picker reports the change, and without
        this that report would be recorded as a fresh step — so the first undo
        would push a step describing itself and the second would undo the undo.
      */
      onEdit: (before, after, label) => {
        if (applying) return;
        history.push({ kind: "selection", label, before, after });
      },
    });
    seamBreaks = createSeamBreaks({
      read: () => {
        const ctx = stack.canvas.getContext("2d");
        if (!ctx) return null;
        const image = ctx.getImageData(0, 0, stack.canvas.width, stack.canvas.height);
        return { width: image.width, height: image.height, data: image.data };
      },
      seam: () => seamMapNow,
    });
    seamOverlay?.destroy();
    seamOverlay = mountSeamOverlay({
      canvas: paintCanvas,
      marks: () => (seamMarksOn ? (seamBreaks?.current()?.marks ?? null) : null),
      enabled: () => seamMarksOn,
      // Every fold, while the tool for thinking about faces is the active one.
      allFolds: () => tools.get().active === "faces",
    });
    // The pairing is async and the overlay is useless without it. Kicked here
    // rather than only on a body or garment CHANGE, or a document opened and
    // left alone would never get one.
    void refreshSeamMap();
    symmetryOverlay = mountSymmetryOverlay({
      canvas: paintCanvas,
      draw: (ctx, hovered) => {
        // The selection first: it is a wash over what cannot be painted, and the
        // guide has to read on top of it rather than under it.
        faceSelect?.draw(ctx, hovered);
        mirrorControls?.drawGuide(ctx, hovered);
      },
    });
    faceList?.destroy();
    faceList = mountFaceList({ faces: faceSelect, garment: garmentForLabel });
    tips?.refresh();
  }

  mountViewControls(rail);

  const buttons = [...document.querySelectorAll<HTMLButtonElement>("[data-tool]")];
  for (const button of buttons) {
    button.addEventListener("click", () => {
      const id = button.dataset.tool as ToolId;
      /*
        A second click on the tool you already have opens its options.

        There used to be a settings button beside the brush, which meant two
        brush-shaped glyphs next to each other doing different jobs: one picked
        the tool, one configured it. Putting the options on the tool itself
        removes the ambiguity and the icon — and it generalises, because the
        eraser has the same settings and had no way to reach them.
      */
      if (tools.get().active !== id) {
        tools.select(id);
        // The Faces tool is what turns every fold on, so the overlay has to
        // hear about a tool change the same way the rail does.
        seamOverlay?.refresh();
        return;
      }
      /*
        Each tool's own options. The faces tool has no size or opacity — what it
        has is a list of the eighteen faces, which is also the only version of
        the picker a keyboard or a screen reader can use.
      */
      if (id === "faces") faceList?.openAt(button);
      else toolOptions?.openAt(button);
    });
  }

  tools.subscribe((state) => {
    for (const button of buttons) {
      button.setAttribute("aria-pressed", String(button.dataset.tool === state.active));
    }
    // The cursor is the only feedback during a stroke, when the rail is out of
    // the corner of your eye and the canvas is not.
    paintCanvas?.closest(".canvas-stage")?.setAttribute("data-tool", state.active);
    // The picker's hover wash is drawn only while its tool is active, so the
    // overlay has to be repainted when that changes.
    symmetryOverlay?.refresh();
    /*
      The body becomes a picker while the faces tool is up.

      The template is an atlas — eighteen rectangles whose names are the thing
      this app exists to stop people getting wrong — and the body is the version
      everybody can already read. Orbit stays live: `pick` only treats a press
      that barely moved as a selection, so gaining the picker never costs the
      ability to turn the model.
    */
    viewer?.setPointerMode(state.active === "faces" ? "pick" : "orbit");
    /*
      Push the brush settings into the painter.

      They were only ever pushed from the options panel's own handlers, so
      anything else that changed them — `[` and `]`, a restored document, a
      future preset — moved the number in the UI and left the brush alone.
      Driving it from the subscription means there is one path: whoever changes
      the state, the painter follows.
    */
    painter.setSize(state.brush.size);
    painter.setOpacity(state.brush.opacity);
    painter.setHardness(state.brush.hardness);
    painter.setFlow(state.brush.flow);
    // Remember where Done goes back to, and offer it only while picking.
    if (state.active !== "faces") toolBeforeFaces = state.active;
    if (state.active !== "eyedropper") toolBeforeSampling = state.active;
    refreshFaceChips();
  });

  // The brush at its real size, under the pointer. Mounted here because the
  // rail is what changes tool, and the ring has to agree with it.
  if (paintCanvas) mountBrushRing({ canvas: paintCanvas, tools });
  /*
    The ring says how big the brush is; this says where it will actually go.

    They are different questions on a body — a brush wider than a sleeve wraps
    around it, and the faces it wraps onto sit elsewhere in the atlas. The ring
    cannot show that and should not try.
  */
  if (paintCanvas) {
    footprint = mountFootprintOverlay({
      canvas: paintCanvas,
      // The same gate the painter uses: a preview of a wrap that will not
      // happen is worse than no preview.
      surface: () => (mirrorControls?.wrapFolds() ? (viewer?.surface() ?? null) : null),
      size: () => tools.get().brush.size,
      crossParts: () => mirrorControls?.crossParts() ?? false,
      selection: () => faceSelect?.selection() ?? EMPTY,
    });
  }

  /*
    Labels for the rail's icons.

    Applied to the container rather than to each button, so it covers every
    control in there — including the two mirroring icons added later, which is
    what prompted it, and the five that had the same problem first. It reads
    the labels off `aria-label` and `title`, so nothing in the markup changes.
  */
  if (rail) tips = mountTips(rail);
  /*
    And the Layers dock, which had none.

    Every icon in a layer row — hide, the two state toggles, the menu — was
    unexplained until you pressed it. The rail has had pinned tips since the
    mirroring icons went in; the panel is the same problem with the same fix.
  */
  const layersDock = document.querySelector<HTMLElement>('.dock[aria-label="Layers"]');
  if (layersDock) mountTips(layersDock);

  /*
    Fabrics. Phase 1 of docs/decisions/fabric-picker-and-gating.md is choosing;
    applying arrives in phase 2, which is why onPick only reports for now.
  */
  /*
    Which preview is current.

    A tile is fetched and decoded, so a slow one can resolve after the pointer
    has moved on — without this, hovering a fabric with a cold cache and moving
    away would leave it stuck on the body, which is worse than never having
    previewed it.
  */
  let previewToken = 0;

  /*
    The fabric layer the last pick created, and the rev it had.

    A second pick supersedes it only while it is untouched: if the user has
    painted on it since, or deleted it, that layer is theirs now and replacing
    it would destroy work. Cleared when the sheet opens, so a fresh visit adds
    rather than replaces.
  */
  let lastFabric: { layerId: string; rev: number } | null = null;

  /*
    The gate.

    Read at the moment of the click rather than captured once: a plan can change
    in another tab, and a stale capture would either refuse a customer who has
    just paid or admit one who has just lapsed.
  */
  const tier = () => identity().tier;

  const fabrics = createFabricPicker({
    isLocked: (entry) => isFabricLocked(entry.id, tier()),
    onLocked(entry) {
      /*
        Straight to the prompt, not a notice that then offers one.

        The taste has already been given: hovering previewed this fabric on
        their own avatar, at full fidelity, and no work has been spent. Then
        they clicked it, which is as clear a statement of intent as this app
        ever gets — answering that with something they have to act on again
        makes them ask twice for the same thing.

        The name is the one the swatch showed, not the raw asset name: being
        told a different thing is locked than the thing you clicked reads as a
        bug rather than a price.
      */
      openUpsell({ reason: lockedMessage(displayName(entry)), tier: "creator" });
    },
    canTint: () => canTint(tier()),
    onTintLocked() {
      openUpsell({ reason: "Recolouring any fabric is part of Creator", tier: "creator" });
    },
    onPreview(entry, tilePx, tint) {
      const token = ++previewToken;
      if (!entry) {
        stack.setPreview(null);
        return;
      }
      void loadTile(entry.id, tint ? "grain" : "colour")
        .then((img) => {
          if (token !== previewToken) return;
          stack.setPreview(previewFor(img, tilePx, tint));
        })
        .catch(() => {
          if (token === previewToken) stack.setPreview(null);
        });
    },
    onPick(entry, tilePx, tint) {
      previewToken += 1;
      stack.setPreview(null);
      const previous =
        lastFabric && stack.get(lastFabric.layerId)?.rev === lastFabric.rev
          ? lastFabric.layerId
          : null;
      void applyFabric(stack, entry, {
        tilePx,
        tint,
        replaceLayerId: previous,
        // Only the in-place fill needs this; adding a layer is a structure
        // change the transaction already records.
        onEdit(layerId, rect, before, after) {
          if (applying) return;
          history.push({
            kind: "pixels",
            label: `Fill with ${entry.name}`,
            layerId,
            rect,
            before,
            after,
            /*
              Read before `applyFabric` marks the layer painted, which is where
              this callback sits — so it is still whatever the layer was made of
              beforehand. An in-place fill deliberately records no new source
              (it is a drawing wearing a fabric, not a fabric), so there is
              nothing to carry forward on redo either.
            */
            fabricBefore: recordBefore(layerId),
          });
        },
      })
        .then((applied) => {
          if (!applied) return;
          lastFabric = applied.inPlace
            ? null
            : { layerId: applied.layer.id, rev: applied.layer.rev };
          announce(applied.inPlace ? `Filled with ${entry.name}` : `Added ${entry.name}`, {
            tone: "success",
            duration: 2500,
          });
        })
        .catch(() => announce(`Could not load ${entry.name}`, { tone: "warn" }));
    },
  });
  /*
    A plan bought elsewhere unlocks this sheet where it stands.

    The upsell sends people to Stripe in another tab; they come back to a sheet
    that was built while they were on free. Without this, every swatch is still
    padlocked and nothing they can do here fixes it — which reads as having paid
    for nothing.
  */
  onIdentityChange(() => fabrics.refresh());

  mountPlaceImage(rail);

  /*
    Handles on the selected placement.

    After the view, because it mounts beside the artwork and the view is what
    puts that inside the transformed wrapper — same as the other two overlays.
  */
  if (paintCanvas) {
    const el = paintCanvas;
    transformOverlay = mountTransformOverlay({
      canvas: el,
      stack,
      viewScale: () => viewControl?.get().scale ?? 1,
      toTemplate: (e) => toTemplate(e, el),
      onCommit(layerId, before, after) {
        /*
          Rewound, then re-applied inside a transaction.

          The drag has already moved the placement — that is what made it a live
          preview rather than a jump on release. `transaction` records the
          layers either side of the call, so putting the old value back first is
          what gives it a `before` to record. The snapshot is a shallow copy and
          every step here ASSIGNS a new placement object rather than mutating
          one, so the copy keeps the old value rather than aliasing the new.
        */
        stack.setPlacement(layerId, before);
        stack.transaction(TRANSFORM_LABEL[transformKind(before, after)], () => {
          stack.setPlacement(layerId, after);
        });
        transformOverlay?.refresh();
      },
    });
  }

  document.querySelector<HTMLButtonElement>("[data-fabrics]")?.addEventListener("click", () => {
    // A fresh visit adds a layer; only picks within one visit supersede.
    lastFabric = null;
    void fabrics.open();
  });

  // Anchored to the brush button as a starting point; `openAt` moves it to
  // whichever tool was clicked.
  const optionsAnchor = document.querySelector<HTMLElement>('[data-tool="brush"]');
  if (optionsAnchor) {
    toolOptions = mountToolOptions({
      /*
        The two reach settings, driven from the one place that owns them.

        `mirrorControls` keeps the state because the painter, the hover preview
        and the chips all read it; this panel is only where you now click it.
      */
      strokeReach: {
        wrapFolds: () => mirrorControls?.wrapFolds() ?? false,
        setWrapFolds: (on) => mirrorControls?.setWrapFolds(on),
        crossParts: () => mirrorControls?.crossParts() ?? false,
        setCrossParts: (on) => mirrorControls?.setCrossParts(on),
        // Below 1024px there is no viewer and no geometry to read a fold from.
        available: () => viewer?.surface() != null,
      },
      anchor: optionsAnchor,
      tools,
      // The panel is where somebody goes when a tool is not behaving, so the
      // reason it is not behaving should be waiting there.
      blockedReason: () => stack.paintBlockedReason(),
      onSize: (px) => painter.setSize(px),
      onOpacity: (fraction) => painter.setOpacity(fraction),
      onHardness: (hardness) => painter.setHardness(hardness),
      onFlow: (flow) => painter.setFlow(flow),
    });
  }

  /*
    Add layer.

    This button shipped with no handler at all — found by auditing every control
    in the shell for a data hook rather than by using the app, which is how a
    dead control survives: it looks exactly like a working one until pressed.
  */
  /*
    The shortcut registry.

    One array, two consumers: installKeys binds it, and the sheet renders it.
    Nothing here is aspirational — a key in the sheet is a key that works,
    because there is no second place to write one down.

    Order is the order they are taught in, and grouping follows first
    appearance. See docs/decisions/keys-and-controls.md for the choices; the
    short version is that every binding that exists elsewhere is spelled the way
    Photoshop spells it, because that is where this audience's fingers come
    from.
  */
  const stepBrush = (delta: number) => {
    const { size } = tools.get().brush;
    // Multiplicative, not additive: +1px is imperceptible at 60 and enormous at
    // 2, which is why every editor that gets this right scales it.
    const next = Math.round(Math.max(1, Math.min(200, size * (delta > 0 ? 1.25 : 0.8))));
    tools.setBrush({ size: next === size ? size + delta : next });
  };

  const bindings: Binding[] = [
    { key: "b", label: "Brush", group: "Tools", run: () => tools.select("brush") },
    { key: "e", label: "Eraser", group: "Tools", run: () => tools.select("eraser") },
    { key: "f", label: "Select faces", group: "Tools", run: () => tools.select("faces") },
    { key: "h", label: "Move the canvas", group: "Tools", run: () => tools.select("pan") },
    /*
      View keys, spelled the way every editor spells them.

      `0` is "show me everything" and is the way back from being lost, which is
      the failure mode zoom introduces — so it is bound before either of the
      others is worth having. The `=` alias is there because the key is
      physically labelled `+` and nobody presses Shift to zoom in.
    */
    /*
      Nudging a placement, which is the only way to be exact.

      A limb face is 64 template pixels wide. Landing a badge on a specific
      texel with a finger, or with a mouse at fit-to-window where one texel is
      one screen pixel, is not a thing anybody can do — so the arrows do it.
      Shift takes ten at a time, the same multiplier every editor uses.

      They fall through to whatever else arrows do when no placement is
      selected, because `nudge` reports whether it took the key.
    */
    ...(
      [
        ["ArrowLeft", -1, 0, "Nudge left"],
        ["ArrowRight", 1, 0, "Nudge right"],
        ["ArrowUp", 0, -1, "Nudge up"],
        ["ArrowDown", 0, 1, "Nudge down"],
      ] as const
    ).flatMap(([key, dx, dy, label]) => [
      {
        key,
        label,
        group: "Image",
        run: () => void transformOverlay?.nudge(dx, dy),
      },
      {
        key,
        shift: true,
        label: `${label} ten`,
        group: "Image",
        run: () => void transformOverlay?.nudge(dx * 10, dy * 10),
      },
    ]),
    { key: "0", label: "Fit to window", group: "View", run: () => viewControl?.fit() },
    { key: "=", label: "Zoom in", group: "View", run: () => viewControl?.zoomBy(1.25) },
    { key: "-", label: "Zoom out", group: "View", run: () => viewControl?.zoomBy(0.8) },
    /*
      Enter finishes selecting, the way it commits a transform in every editor
      that has one. It does nothing outside the picker, so it costs no other
      binding.
    */
    { key: "Enter", label: "Finish selecting", group: "Tools", run: () => doneSelecting() },
    /*
      Deselect, from ANY tool — the point being that you should not have to go
      back to the picker to get rid of a selection you have finished with. The
      chip's x does the same job for a pointer; this is for the keyboard.
    */
    {
      key: "d",
      mod: true,
      label: "Deselect faces",
      group: "Edit",
      run: () => faceSelect?.clear(),
    },
    {
      key: "a",
      mod: true,
      label: "Select all faces",
      group: "Edit",
      run: () => faceSelect?.selectAll(),
    },
    {
      key: "i",
      mod: true,
      shift: true,
      label: "Invert face selection",
      group: "Edit",
      run: () => faceSelect?.invertSelection(),
    },
    { key: "[", label: "Smaller brush", group: "Tools", run: () => stepBrush(-1) },
    { key: "]", label: "Bigger brush", group: "Tools", run: () => stepBrush(1) },

    { key: "z", mod: true, label: "Undo", group: "Edit", run: () => runHistory("undo") },
    {
      key: "z",
      mod: true,
      shift: true,
      label: "Redo",
      group: "Edit",
      run: () => runHistory("redo"),
    },
    // Windows spells redo ⌃Y. Listing it beside ⇧⌘Z would imply a difference.
    {
      key: "y",
      mod: true,
      alias: true,
      label: "Redo",
      group: "Edit",
      run: () => runHistory("redo"),
    },

    {
      key: "n",
      mod: true,
      shift: true,
      label: "New layer",
      group: "Layers",
      run: () => {
        const n = stack.layers().filter((l) => l.kind === "artwork").length + 1;
        stack.transaction("Add layer", () => stack.addArtwork({ name: `Layer ${n}` }));
      },
    },
    {
      key: "j",
      mod: true,
      label: "Duplicate layer",
      group: "Layers",
      run: () => {
        const active = stack.active();
        if (active) stack.transaction("Duplicate layer", () => stack.duplicate(active.id));
      },
    },
    {
      key: "e",
      mod: true,
      label: "Merge down",
      group: "Layers",
      run: () => {
        const active = stack.active();
        if (!active) return;
        const result = stack.transaction("Merge down", () => stack.mergeDown(active.id));
        // Refusing in silence is how a shortcut earns a reputation for being
        // broken. The menu explains itself; so does this.
        if (result && !result.ok) announce(result.reason, { tone: "warn" });
      },
    },

    {
      key: "?",
      shift: true,
      display: "?",
      label: "Keyboard shortcuts",
      group: "Help",
      run: () => sheet.toggle(),
    },
  ];

  const sheet = shortcutsSheet(bindings, IS_MAC);
  installKeys(bindings, { isMac: IS_MAC });

  // The Help button and `?` open the same sheet. This button has been wired to
  // nothing since the shell was built.
  document.querySelector<HTMLButtonElement>("[data-help]")?.addEventListener("click", () => {
    sheet.toggle();
  });

  mountHistoryButtons();

  const addLayer = document.querySelector<HTMLButtonElement>("[data-add-layer]");
  addLayer?.addEventListener("click", () => {
    const n = stack.layers().filter((l) => l.kind === "artwork").length + 1;
    stack.transaction("Add layer", () => stack.addArtwork({ name: `Layer ${n}` }));
  });

  /*
    Duplicate and Delete, acting on the selected layer.

    Promoted out of the row menu because they are frequent enough to deserve a
    permanent target, and kept out of the row itself because that target would
    sit next to a drag handle on a 44px line that scrolls under a thumb. Both
    also remain in the ⋯ menu: the header acts on the selection, the menu acts
    on the row you opened it from, and those are different intentions.
  */
  const duplicateLayer = document.querySelector<HTMLButtonElement>("[data-duplicate-layer]");
  duplicateLayer?.addEventListener("click", () => {
    const active = stack.active();
    if (active) stack.transaction("Duplicate layer", () => stack.duplicate(active.id));
  });

  const deleteLayer = document.querySelector<HTMLButtonElement>("[data-delete-layer]");
  deleteLayer?.addEventListener("click", () => {
    const active = stack.active();
    // No confirmation, deliberately. ⌘Z restores the layer and its pixels, and a
    // dialog on every delete is the tax you pay for an undo you do not trust.
    if (active) stack.transaction("Delete layer", () => stack.remove(active.id));
  });

  syncLayerActions = () => {
    const count = stack.layers().length;
    const active = stack.active();
    if (duplicateLayer) {
      duplicateLayer.disabled = !active;
      duplicateLayer.title = active ? `Duplicate ${active.name}` : "Select a layer first";
    }
    if (deleteLayer) {
      deleteLayer.disabled = !active || count <= 1;
      // The reason lives in the title, because a greyed control that explains
      // nothing is a dead end — the same rule the ⋯ menu follows.
      deleteLayer.title =
        count <= 1
          ? "A design needs at least one layer"
          : active
            ? `Delete ${active.name}`
            : "Select a layer first";
    }
  };
  syncLayerActions();
}

// ------------------------------------------------------------------ import

/**
 * The front door.
 *
 * Most art arrives from Procreate, Photoshop or ibis Paint, and none of those
 * can show it on a body. Getting a file in has to be as prominent as getting one
 * out — hence a real button beside Export, a drop target over the canvas, and a
 * live link that re-reads the file every time the artist saves.
 *
 * Statically imported, unlike the viewer. import.ts depends only on the region
 * map — a few kB — so splitting it buys nothing and costs a real failure mode:
 * the first version wired the drop target inside `import().then()`, and a drop
 * that arrived before that promise settled was silently swallowed.
 */
/**
 * Re-fill a fabric layer one step coarser or finer.
 *
 * Steps by a factor rather than a fixed number of pixels, because weave is
 * judged as a ratio: going 90 to 106 is invisible on a chest, and the same
 * sixteen pixels is the whole difference between a 24 and a 40. A quarter each
 * way is roughly the smallest step that reads as a change.
 *
 * Not gated. Scale is not the paid line — tint is — and a fill someone already
 * has should stay adjustable.
 */
function reweaveLayer(layerId: string, direction: 1 | -1) {
  const layer = stack.get(layerId);
  const source = layer ? fabricOf(layer) : null;
  if (!layer || !source) return;

  const stepped = direction > 0 ? source.tilePx * 1.25 : source.tilePx / 1.25;
  const tilePx = Math.round(Math.min(MAX_TILE_PX, Math.max(MIN_TILE_PX, stepped)));
  if (tilePx === source.tilePx) return;

  const ctx = layer.source.getContext("2d");
  const full: DirtyRect = { x: 0, y: 0, w: CANVAS.width, h: CANVAS.height };
  const before = ctx?.getImageData(0, 0, full.w, full.h) ?? null;

  void refillLayer(stack, layerId, withTilePx(source, tilePx)).then((ok) => {
    if (!ok) return;
    renderPanel();
    const after = ctx?.getImageData(0, 0, full.w, full.h) ?? null;
    if (before && after) {
      history.push({
        kind: "pixels",
        label: `Weave ${tilePx} px`,
        layerId,
        rect: full,
        before,
        after,
        fabricBefore: source,
        fabricAfter: stack.get(layerId)?.fabric ?? null,
      });
    }
    announce(`${source.name} · weave ${tilePx} px`, { tone: "info", duration: 1600 });
  });
}

/**
 * Pick up the colour already on the garment.
 *
 * Read from `stack.canvas` — the composite of the layers — rather than from the
 * on-screen canvas, which has the template guide drawn under the artwork at 35%
 * opacity. Sampling that returns a blend of the two: a colour that is nowhere in
 * the file, cannot be matched by a second sample, and exports as something else
 * again.
 *
 * The composite rather than the active layer, for the same reason the flood
 * fill samples it: what someone is pointing at is what they can see, and on a
 * layer above the artwork the layer itself is transparent there.
 */
function sampleColour(x: number, y: number) {
  const px = Math.round(x);
  const py = Math.round(y);
  if (px < 0 || py < 0 || px >= CANVAS.width || py >= CANVAS.height) return;

  const ctx = stack.canvas.getContext("2d");
  if (!ctx) return;
  const [r, g, b, a] = ctx.getImageData(px, py, 1, 1).data;

  /*
    Nothing there is not a colour.

    Transparent texels carry whatever RGB happened to be left under them, which
    on a cleared canvas is black. Picking up black from an empty sleeve and
    silently loading the brush with it is the kind of wrong that is only noticed
    two strokes later.
  */
  if ((a ?? 0) === 0) {
    announce("Nothing to pick up there.", { tone: "info", duration: 1800 });
    return;
  }

  const hex = toHex({ r: r ?? 0, g: g ?? 0, b: b ?? 0 });
  // Through the picker, so its own swatch and recents follow. It fires the same
  // onChange a manual pick does, which is what sets `currentColour`.
  if (colourPicker) colourPicker.setColour(hex);
  else {
    currentColour = hex;
    painter.setColour(hex);
  }
  announce(`Picked ${hex}`, { tone: "success", duration: 1600 });
  // Back to whatever was in hand. Sampling is a step inside another job.
  tools.select(toolBeforeSampling);
}

/**
 * How far past the tolerance a flood still paints, faintly.
 *
 * Fixed rather than exposed, and deliberately not derived from `tolerance`:
 * they are different controls. Tolerance decides *where* the edge falls — turn
 * it up and the fill claims more. Feather decides how *hard* that edge is, and
 * wanting a soft edge is not the same wish as wanting a bigger fill. Tying one
 * to the other would mean a crisp fill could only ever be a small one.
 *
 * 24 is one ramp's worth on antialiased artwork. Only texels touching the fill
 * are affected, so the cost of it being slightly wrong is a single row of
 * texels a little too faint or a little too strong.
 */
const FLOOD_FEATHER = 24;

/**
 * Fill, in one tap.
 *
 * Two behaviours behind one tool. "Whole face" is the default because it is the
 * one no other editor can offer — "fill the left sleeve" needs the region map —
 * and because a base colour is the first thing anyone lays down. "Matching
 * colour" is the classic flood, bounded to the face under the tap.
 *
 * Both refuse a face the user has excluded, for the same reason a stroke does:
 * a selection that a tap can walk around is not a selection.
 */
function applyFill(x: number, y: number) {
  const region = regionAt(Math.round(x), Math.round(y));
  // Dead space between the faces. Nothing to fill and nothing to say.
  if (!region) return;

  /*
    Refuse out loud, the way the brush does.

    activeCtx() returns null for a hidden layer, a zero-opacity one and a
    placement — and a fill that silently does nothing is indistinguishable from
    a broken tool. The hidden-layer case is the cruel one: on a stroke the paint
    is at least written where it cannot be seen, but a fill here writes nothing
    at all. paintBlockedReason has known the answer since the stack was written.
  */
  const layer = stack.active();
  const ctx = stack.activeCtx();
  if (!layer || !ctx) {
    const why = stack.paintBlockedReason();
    if (why) announce(why, { tone: "warn" });
    return;
  }

  const selection = faceSelect?.selection() ?? EMPTY;
  if (!allows(selection, region)) {
    announce("That face is not selected.", { tone: "info", duration: 1800 });
    return;
  }

  const { mode, tolerance } = tools.get().fill;

  /*
    Which faces this tap covers.

    One for a face fill or a flood; every side of the limb for a part fill. The
    selection filters the list rather than refusing the tap, so filling a sleeve
    with only its front chosen fills the front — the same rule a stroke follows.
  */
  const faces =
    mode === "part"
      ? partFaces(region, Object.keys(REGIONS) as RegionId[]).filter((id) => allows(selection, id))
      : [region];
  const rects = faces.map((id) => faceFillRect(REGIONS[id], GUTTER_PX));
  const rect = unionRect(rects) ?? faceFillRect(REGIONS[region], GUTTER_PX);
  /*
    Symmetry is a property of the STROKE, and a fill is a stroke you did not
    drag. Leaving it out meant turning symmetry on and tapping fill produced
    half a garment with the toggle still lit — the least obvious way for a mode
    to be half-implemented, because the tool is the wrong one rather than the
    switch.
  */
  const fillMirror = mirrorControls?.enabled() ? (viewer?.mirrorMap() ?? null) : null;
  // Both halves, or undo restores one and leaves the other behind.
  const bounds = clampToCanvas(fillBounds(rect, fillMirror));
  const before = ctx.getImageData(bounds.x, bounds.y, bounds.w, bounds.h);

  /*
    Alpha lock applies to a fill exactly as it does to a stroke.

    `source-atop` keeps paint inside what the layer already has. Without it,
    "paint inside only" was a promise the brush kept and the fill broke — one
    tap and the colour is outside the shape the lock exists to protect.
  */
  const composite = strokeCompositeMode({
    erasing: false,
    paintInsideOnly: Boolean(layer.paintInsideOnly),
  });

  /*
    Drawn onto a scratch and mirrored there, then composited once.

    Not mirrored afterwards by copying finished texels: that bypasses
    `composite`, so the alpha lock would hold on the tapped face and not on its
    mirror — colour outside the shape the lock exists to protect. Same reasoning
    as paint.ts, and the same call.
  */
  const paintFill = (draw: (g: CanvasRenderingContext2D) => void) =>
    fillWithMirror({
      target: ctx,
      rect,
      draw,
      composite,
      mirror: fillMirror,
      // A fill is not a way around the face selection either.
      allow: (fx, fy) => allowsAt(selection, fx, fy),
    });

  if (mode !== "colour") {
    paintFill((g) => {
      g.fillStyle = currentColour;
      // Face by face, never the union: the union of a limb's faces spans the
      // dead space between them, and filling that writes colour nothing can
      // sample.
      for (const r of rects) g.fillRect(r.x, r.y, r.w, r.h);
    });
  } else {
    /*
      Flooded from the composite, not from the layer.

      What the user is pointing at is what they can see, and on a layer above
      artwork the layer itself is transparent there. Sampling the layer would
      flood a colour they are not looking at.
    */
    const source = stack.canvas.getContext("2d");
    if (!source) return;
    const seen = source.getImageData(0, 0, CANVAS.width, CANVAS.height);
    const found = floodMask({
      data: seen.data,
      width: CANVAS.width,
      height: CANVAS.height,
      x: Math.round(x),
      y: Math.round(y),
      tolerance,
      bounds: rect,
      feather: FLOOD_FEATHER,
    });
    if (!found) return;
    // Painted through the coverage on a scratch, then drawn once — a fillRect
    // per texel would be tens of thousands of calls for one tap.
    const patch = new ImageData(found.rect.w, found.rect.h);
    const rgb = hexToRgb(currentColour);
    for (let i = 0; i < found.coverage.length; i++) {
      const alpha = found.coverage[i] as number;
      // Coverage, not the mask: the fringe is the whole point, and it is the
      // texels the mask says no to.
      if (alpha === 0) continue;
      const p = i * 4;
      patch.data[p] = rgb[0];
      patch.data[p + 1] = rgb[1];
      patch.data[p + 2] = rgb[2];
      patch.data[p + 3] = alpha;
    }
    const scratch = document.createElement("canvas");
    scratch.width = found.rect.w;
    scratch.height = found.rect.h;
    scratch.getContext("2d")?.putImageData(patch, 0, 0);
    paintFill((g) => g.drawImage(scratch, found.rect.x, found.rect.y));
  }

  stack.markPainted(layer.id);
  stack.compose(bounds);
  const after = ctx.getImageData(bounds.x, bounds.y, bounds.w, bounds.h);
  history.push({
    kind: "pixels",
    label: mode === "part" ? "Fill part" : mode === "face" ? "Fill face" : "Fill colour",
    layerId: layer.id,
    rect: bounds,
    before,
    after,
    fabricBefore: layer.fabric,
  });
  for (const g of groupsOf(garment)) viewer?.invalidate(g);
}

/** `#rgb` or `#rrggbb` to a triple. Local; the picker's parser carries HSV. */
function hexToRgb(hex: string): [number, number, number] {
  let h = hex.trim().replace(/^#/, "");
  if (h.length === 3) h = (h[0] ?? "") + h[0] + h[1] + h[1] + h[2] + h[2];
  const n = Number.parseInt(h, 16);
  if (h.length !== 6 || Number.isNaN(n)) return [0, 0, 0];
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/*
  The colour dot on a fabric layer.

  Live while the colour is dragged, one history entry when it settles. The
  picker streams onChange continuously, and re-filling is a handful of
  fillRects against a pattern — cheap enough to do on every one, which is the
  point: the fabric on the body should move with the cursor.

  History is the opposite: an entry per animation frame would bury every
  earlier edit under a hundred colour steps. So the layer's pixels are
  captured once when the picker opens and pushed once when it closes, the
  same shape the brush already uses.
*/
function recolourLayer(layerId: string) {
  const layer = stack.get(layerId);
  const source = layer ? fabricOf(layer) : null;
  if (!layer || !source) return;

  // Same gate and same words as the fabric sheet's own colour control. Read
  // now rather than captured, so a plan bought in another tab counts.
  if (!canTint(identity().tier)) {
    openUpsell({ reason: `Recolouring ${source.name} is part of Creator`, tier: "creator" });
    return;
  }

  // Queried now rather than closed over: rows are reconciled, so the
  // element behind a layer id outlives nothing in particular.
  const anchor = document.querySelector<HTMLElement>(
    `[data-layers] [data-layer="${CSS.escape(layerId)}"] .layer-tint`,
  );
  if (!anchor) return;

  const ctx = layer.source.getContext("2d");
  const full: DirtyRect = { x: 0, y: 0, w: CANVAS.width, h: CANVAS.height };
  const before = ctx?.getImageData(0, 0, full.w, full.h) ?? null;
  /** The record these pixels carry, so undo can put it back with them. */
  const fabricBefore = source;

  /*
    What the layer is showing *now*, not what it showed when the picker opened.

    Comparing against the opening state looks equivalent and is not: drag red to
    green and back to red and the last step matches the original, so it would be
    skipped as a no-op and the layer would sit on green while the picker showed
    red.
  */
  let current = source;
  let changed = false;
  /*
    Re-fills run one at a time, newest wins.

    Each one awaits loadTile, so two started in quick succession can resolve out
    of order and leave the layer on a colour the user has already dragged past.
    Chaining keeps them ordered; the sequence check drops any that a newer pick
    has already superseded while it waited.
  */
  let seq = 0;
  let pending: Promise<void> = Promise.resolve();

  const picker = createColourPicker({
    anchor,
    initial: source.tint ?? "#E2231A",
    onChange: (hex) => {
      const next = withTint(current, hex);
      if (!differs(current, next)) return;
      current = next;
      const mine = ++seq;
      pending = pending.then(async () => {
        if (mine !== seq) return;
        const ok = await refillLayer(stack, layerId, next);
        // Only a re-fill that actually landed counts as a change worth undoing.
        if (!ok) return;
        changed = true;
        renderPanel();
      });
    },
    onClose: () => {
      // Destroyed either way: the picker is built per click, so leaving it
      // attached would stack a new popover on the anchor every time.
      picker.destroy();
      /*
        Wait for the last re-fill before reading the result.

        Closing the picker does not cancel work already in flight, and capturing
        `after` synchronously could record pixels that are about to be replaced
        — an undo entry describing a state the layer never rested in.
      */
      void pending.then(() => {
        if (!changed || !before) return;
        const live = stack.get(layerId);
        const after = live?.source.getContext("2d")?.getImageData(0, 0, full.w, full.h);
        // Deleted mid-pick: there is nothing left to undo onto.
        if (!live || !after) return;
        history.push({
          kind: "pixels",
          label: `Recolour ${source.name}`,
          layerId,
          rect: full,
          before,
          after,
          fabricBefore,
          fabricAfter: live.fabric ?? null,
        });
      });
    },
  });
  picker.open();
}

function wireImport() {
  const stage = document.querySelector<HTMLElement>("[data-drop]");
  const button = document.querySelector<HTMLButtonElement>("[data-import]");
  const report = document.querySelector<HTMLElement>("[data-source-report]");
  const hint = document.querySelector<HTMLElement>("[data-source-hint]");
  const liveButtons = document.querySelectorAll<HTMLButtonElement>(
    "[data-live-link], [data-live-link-inline]",
  );

  let link: { filename: string; stop(): void } | null = null;

  const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  const render = (source: string, r: ImportReport, live: boolean) => {
    if (!report) return;
    const fitWord = { exact: "1:1", rescale: "rescaled", contain: "centred" }[r.fit];
    const filled = 18 - r.emptyRegions.length;
    const notes = r.notes.map((n) => `<p class="source-note" data-warn>${esc(n)}</p>`).join("");
    report.innerHTML =
      `<dl class="source-line"><dt>File</dt><dd>${live ? '<span class="live-dot"></span>' : ""}${esc(source)}</dd></dl>` +
      `<dl class="source-line"><dt>Fit</dt><dd>${r.sourceWidth}×${r.sourceHeight} · ${fitWord}</dd></dl>` +
      `<dl class="source-line"><dt>Faces</dt><dd>${filled} of 18 have art</dd></dl>` +
      (live ? '<p class="source-note">Watching. Save in your paint app to update.</p>' : "") +
      notes;
    report.hidden = false;
    if (hint) hint.hidden = true;
  };

  const fail = (message: string) => {
    if (!report) return;
    report.innerHTML = `<p class="source-note" data-warn>${esc(message)}</p>`;
    report.hidden = false;
  };

  /**
   * The layer a live link is feeding, so a save on disk updates *that* layer.
   *
   * This is the whole reason the link belongs to a layer rather than to the
   * document. Replacing the canvas on every tick — which is what the first
   * version did — wiped every logo stacked above the base texture each time the
   * artist pressed save in Photoshop.
   */
  let linkedLayerId: string | null = null;

  const accept = (result: ImportResult, source: string, live: boolean) => {
    if (live && linkedLayerId && stack.get(linkedLayerId)) {
      stack.setPixels(linkedLayerId, result.bitmap, CANVAS.width, CANVAS.height);
    } else {
      // Adds, never replaces. Nothing anyone made is destroyed by an import.
      const layer = stack.addArtwork({
        name: source,
        image: result.bitmap,
        ...(live ? { link: { filename: source } } : {}),
      });
      if (live) linkedLayerId = layer.id;
    }
    result.bitmap.close();
    render(source, result.report, live);
  };

  const takeFile = async (file: File) => {
    try {
      accept(await importFile(file), file.name, false);
    } catch (err) {
      console.error("import failed", err);
      fail(err instanceof Error ? err.message : "Could not read that file.");
    }
  };

  // A hidden input rather than showOpenFilePicker for the button: it works in
  // every browser, and the live link below is where the fancy API earns its
  // feature detection.
  const picker = document.createElement("input");
  picker.type = "file";
  picker.accept = "image/png,image/jpeg,image/webp";
  picker.addEventListener("change", () => {
    const file = picker.files?.[0];
    // Reset so choosing the same file twice still fires a change event.
    picker.value = "";
    if (file) void takeFile(file);
  });
  button?.addEventListener("click", () => picker.click());

  if (stage) {
    wireDropTarget(
      stage,
      (file) => void takeFile(file),
      (over) => stage.toggleAttribute("data-dropping", over),
    );
  }

  for (const b of liveButtons) {
    b.addEventListener("click", async () => {
      if (link) {
        link.stop();
        link = null;
        // The layer stays — unlinking stops the updates, it does not undo the
        // import. Clearing the id means a later link creates its own layer
        // instead of quietly overwriting this one's pixels.
        linkedLayerId = null;
        if (report) report.hidden = true;
        if (hint) hint.hidden = false;
        return;
      }
      if (!liveLinkSupported()) {
        fail("This browser cannot watch files. Drag the PNG onto the canvas instead.");
        return;
      }
      try {
        link = await linkLiveFile((result, name) => accept(result, name, true), fail);
      } catch (err) {
        fail(err instanceof Error ? err.message : "Could not watch that file.");
      }
    });
  }
}

// ------------------------------------------------------------------ export

function wireExport() {
  const button = document.querySelector<HTMLButtonElement>("[data-export]");
  const status = document.querySelector<HTMLElement>("[data-export-status]");
  const title = document.querySelector<HTMLInputElement>(".rename");
  if (!button) return;

  const say = (text: string, bad = false) => {
    if (!status) return;
    status.textContent = text;
    if (bad) status.setAttribute("data-bad", "");
    else status.removeAttribute("data-bad");
  };

  /*
   * Export opens the sheet rather than downloading.
   *
   * It used to run the pipeline and hand over a file, reporting afterwards what
   * it had done — numbers that arrive once the download has happened cannot
   * inform anything. The sheet moves the same numbers in front of the decision,
   * and every one of them is measured from the exact bytes its button hands
   * over, because `prepareExport` runs the pipeline once and the sheet holds
   * the result.
   *
   * Still one click for the common case: the sheet opens already answered.
   */
  button.addEventListener("click", async () => {
    button.disabled = true;
    say("");
    try {
      const { openExportSheet } = await import("./export-sheet");
      await openExportSheet({
        source: stack.canvas,
        title: title?.value ?? "untitled",
        garment,
        /*
          Seam bleed needs geometry, and the export must not depend on whether
          the 3D pane happens to be open — below 1024px it never is. The map is
          built straight from the GLB, no viewer involved. See seam-source.ts.
        */
        body: bodyId,
        // Changing the garment here changes the document, not just the file —
        // the guide picture and the 3D preview have to agree with what is about
        // to be uploaded.
        onGarment: (next) => applyGarment(next, true),
        /*
          The badges are the placement layers, and the count is read now rather
          than captured at boot: somebody may have added the eighth rank since
          the editor opened.
        */
        rankSet: {
          count: stack.layers().filter((l) => l.kind === "placement").length,
          run: () => exportRankSet(title?.value ?? "untitled", garment, bodyId),
        },
      });
      if (title) title.value = autosave?.title() ?? title.value;
    } catch (err) {
      console.error("export failed", err);
      say("export failed", true);
    } finally {
      button.disabled = false;
    }
  });
}

// ------------------------------------------------------------------ viewer

function showFallback(message: string) {
  if (!mount) return;
  mount.innerHTML = "";
  const el = document.createElement("div");
  el.className = "viewer-fallback";
  el.textContent = message;
  mount.appendChild(el);
}

async function mountViewer() {
  if (!mount || viewer || mounting) return;
  mounting = true;
  try {
    const { mountViewer: mountFn } = await import("../viewer/index");
    viewer = await mountFn(mount, {
      body: "r6-classic",
      mode: "flat",
      /*
        Choosing a face on the body itself.

        The 2D picker points at an atlas; this points at the thing the atlas is
        a picture of. It routes through the SAME `FaceSelect` methods the
        template picker uses, so both surfaces record the same history and there
        is still one selection — the rule the checklist was held to as well.

        Granularity comes along for free: Part mode takes the whole strip here
        too, so what a tap means does not change with where you tapped.
      */
      onFacePick: ({ region }) => {
        if (!faceSelect?.picking()) return;
        if (faceSelect.granularity() === "part") faceSelect.toggleLimb(groupOf(region));
        else faceSelect.toggle(region);
      },
      /*
        Show which face a click would take, before it takes it.

        The template picker washes the face under the pointer; this is that, on
        the body. Cleared on leave, and never drawn while another tool is up —
        a highlight that outlives its tool reads as a stuck selection.
      */
      onFaceHover: (hit) => {
        if (!faceSelect?.picking()) return;
        viewer?.highlightRegion(hit ? hit.region : null);
      },
    });

    /*
      The live canvas, not a snapshot. Every group this garment covers reads the
      same surface, because one 585×559 template feeds all of them.

      Bound per garment, not all three at once. The obvious version — bind
      everything up front, on the grounds that the viewer only samples what the
      garment covers — is wrong, and invisible until you switch: a group keeps
      rendering the last texture uploaded to it. Design a shirt, switch to
      pants, and the arms carry on wearing the shirt's fabric over a template
      that no longer contains it.
    */
    bindSurfaces();
    // The viewer has been garment-aware since it was written and nothing ever
    // told it which one. Without this a pants document previews on a shirt.
    viewer.setGarment(garment === "pants" ? "pants" : "shirt");

    /*
      The pane caption becomes the body picker.

      It used to read `r6-classic` — an internal key, and the only thing on
      screen about which body you were looking at or how far to trust it. The
      picker replaces it with a name plus a one-word fidelity chip, and lets you
      change body without leaving the editor.

      Mounted here rather than at boot because the 3D pane does not exist below
      1024px, and a control for a preview that is not showing is worse than no
      control.
    */
    if (label) {
      label.textContent = "";
      const { mountBodyPicker } = await import("./body-picker");
      picker = await mountBodyPicker({
        mount: label,
        viewer,
        current: bodyId,
        onChange: (id) => {
          bodyId = id;
          // Which faces meet is a fact about a rig, so the pairing is too.
          void refreshSeamMap();
        },
      });

      /*
        Mirroring goes in the tool rail, not here beside the picker.

        It is mounted on the viewer's lifetime because the pairing is derived
        from the body and there is none without one — but that is the only thing
        it shares with the preview. What it changes is a stroke, so it lives
        where the strokes are made. See src/app/mirror-controls.ts.

        The viewer is captured through the module-level `viewer` rather than the
        local, so a body switch does not leave the controls holding a disposed
        handle — `mirrorMap()` is re-asked each time and returns the current
        body's pairing.
      */
      /*
        The occlusion ghost, on the viewer's lifetime for the same reason.

        It holds the set of parts it has faded, and that set means nothing
        against another body's meshes — so it is rebuilt with the viewer rather
        than kept across a switch.

        Built here and immediately given the current selection, because the
        picker mounts at boot and works with no geometry: somebody can have
        faces chosen before a body finishes loading, and the body should arrive
        already showing them rather than waiting for the next change.
      */
      ghost = createGhostController(viewer);
      const picked = faceSelect?.selection();
      if (picked?.size) ghost.revealAll([...picked]);

      const { mountMirrorControls } = await import("./mirror-controls");
      mirrorControls = mountMirrorControls({
        mirror: () => viewer?.mirrorMap() ?? null,
        // The chip is the state; the button is the control. Driven from one
        // callback so the two cannot disagree.
        onChange: (on) => {
          modeChips?.set(SYMMETRY_CHIP, on);
          // The guide appears and disappears with the mode, and nothing else
          // is going to repaint the overlay on its own.
          symmetryOverlay?.refresh();
        },
        target: () => {
          const layer = stack.active();
          const ctx = layer?.source.getContext("2d");
          return layer && ctx ? { id: layer.id, ctx } : null;
        },
        putPixels: (id, rect, data) => stack.putPixels(id, rect, data),
        // The one-shot writes its pixels and then records, so the layer's
        // record is already stale by here and still describes the `before`.
        record: (edit) =>
          history.push({ kind: "pixels", ...edit, fabricBefore: recordBefore(edit.layerId) }),
        announce: (message, action) => announce(message, { action, tone: "success" }),
        undo: () => runHistory("undo"),
        // One selection, obeyed by the live mode and the one-shot alike — the
        // same value the painter reads.
        selection: () => faceSelect?.selection() ?? EMPTY,
        // Cheap, and deliberately not `mirrorMap() !== null`: that builds the
        // map on first call, and the joints toggle needs no map to know whether
        // there is a body under the template.
        hasBody: () => viewer?.surface() != null,
        onCrossPartsChange: (on) => modeChips?.set(JOINTS_CHIP, on),
        onWrapFoldsChange: (on) => modeChips?.set(WRAP_CHIP, on),
      });
      // These arrived after the rail was labelled, and a `title` left in place
      // would fire the native tooltip on top of ours.
      tips?.refresh();

      // The overlay is mounted at boot now, not here, because the face picker
      // draws on it too and needs no geometry. Nudged so the symmetry guide
      // appears the moment its map exists.
      symmetryOverlay?.refresh();
    }
  } catch (err) {
    console.error("viewer failed to mount", err);
    showFallback("Preview unavailable");
  } finally {
    mounting = false;
  }
}

function unmountViewer() {
  picker?.destroy();
  picker = null;
  // Leaves no key listener behind: the toggle binds `S` on the document, and a
  // shortcut for a control that is gone is worse than no shortcut. It also
  // takes its rule out of the rail, so the tools do not end on a divider.
  mirrorControls?.destroy();
  mirrorControls = null;
  /*
    Dropped, not cleared. `clear()` would tween parts on a viewer that is being
    disposed; the next body arrives whole regardless, and the new controller
    starts with an empty faded set.
  */
  ghost = null;
  // The controls are gone, so the modes are gone. A chip claiming otherwise
  // would be lying about a control that no longer exists.
  modeChips?.set(JOINTS_CHIP, false);
  // The overlay outlives the viewer — the face picker draws on it without any
  // geometry — so it is only refreshed here, not destroyed. `drawGuide` returns
  // early with no map, so the symmetry half simply stops drawing.
  symmetryOverlay?.refresh();
  // The toggle is gone, so the mode is gone. A chip claiming otherwise would be
  // lying about a control that no longer exists.
  modeChips?.set(SYMMETRY_CHIP, false);
  viewer?.dispose();
  viewer = null;
  if (mount) mount.innerHTML = "";
  // The picker owned the caption; leave it empty rather than restoring a key
  // nobody should have been reading in the first place.
  if (label) label.textContent = "";
}

// -------------------------------------------------------------------- boot

async function boot() {
  if (!paintCanvas) return;

  // The shirt guide up front so the canvas is never blank while the archive is
  // read; `applyGarment` below swaps it if the document turns out to be pants.
  await loadGuide(GUIDE_SRC.shirt);
  // One empty artwork layer to start, so the first brush stroke has somewhere to
  // land. Without it `activeCtx()` is null and painting silently does nothing —
  // which looks exactly like the brush being broken.
  const swatch = document.querySelector<HTMLElement>("[data-colour]");
  if (swatch) {
    // Held, so the eyedropper can push a sampled colour back through the same
    // path a manual pick takes — one value, one writer, and the swatch, the
    // picker's own state and the painter cannot drift apart.
    // No click handler needed: the picker attaches its own popover to this
    // anchor. Binding a second one here toggles twice per click, which reads as
    // a popover that never closes — it shuts and reopens inside one gesture.
    colourPicker = createColourPicker({
      anchor: swatch,
      initial: BRUSH_COLOUR,
      onChange: (hex) => {
        currentColour = hex;
        painter.setColour(hex);
      },
    });
  }

  const panelHost = document.querySelector<HTMLElement>("[data-layers]");
  if (panelHost) {
    const panel = mountLayersPanel(panelHost, {
      stack,
      // The dock header, so blend and opacity cost the layer list no height.
      propsSlot: document.querySelector<HTMLElement>("[data-layer-props]") ?? undefined,
      onRecolour: recolourLayer,
      onWeave: reweaveLayer,
      onReplace: (id) => chooseImage((file) => void replaceImageFor(id, file)),
      onBlur: blurLayer,
      onSelect: (id) => {
        // Selecting a placement is when its handles should appear. Nothing
        // composites on a selection, so without this the canvas does not
        // change and clicking a row looks like it did nothing.
        transformOverlay?.refresh();
        // What the options panel says depends on the selected layer now, not
        // only on the tool.
        toolOptions?.refresh();
        /*
          Choosing an image leaves the face picker.

          Face selection is a scope for PAINTING and a placement cannot be
          painted on, so the picker has nothing to say about one — staying in
          it means every press goes to picking faces while the handles sit
          there looking operable. Dropping back to whatever tool was in hand
          before the picker is the same exit `Done` uses.
        */
        const layer = id ? stack.get(id) : null;
        if (layer?.kind === "placement" && tools.get().active === "faces") {
          doneSelecting();
          announce(`Face picking does not apply to ${layer.name}.`, {
            tone: "info",
            duration: 2600,
          });
        }
      },
      // The one selection, read rather than copied — see PanelOptions.selection.
      selection: () => faceSelect?.selection() ?? EMPTY,
      // The mirror one-shots, from the group that owns the map they need.
      layerItems: () => mirrorControls?.layerItems() ?? [],
    });
    renderPanel = panel.render;
  }

  /*
   * Autosave, and the restore that comes with it.
   *
   * Installed before the default layer is added, and the default is added only
   * when nothing came back — otherwise every reload would append an empty
   * "Artwork" on top of the restored stack, which reads as the editor quietly
   * adding junk to your file.
   *
   * `touchSave` is assigned only after this resolves, so the composites that
   * hydrate() triggers on the way in do not schedule a save of a half-restored
   * document over the archive being restored from.
   */
  /*
   * `?new` and `?p=<id>` are commands, not state, and they are spent here.
   *
   * `installAutosave` remembers whichever design it opened, so a reload resumes
   * it from storage with no query string at all. Leaving the command in the
   * address bar would make reloading `?new` mint another blank design every
   * time — and the back button, which restores a URL rather than a session,
   * would do it too.
   *
   * Stripped before the await rather than after: an await is a place the user
   * can press reload, and the window where the URL still says `new` should be
   * as close to zero as it can be.
   */
  const open = openIntentFrom(window.location.search);
  if (open.kind !== "resume") {
    // `window.history`, spelled out: `history` in this module is the undo stack.
    window.history.replaceState(null, "", window.location.pathname);
  }

  // Assigned to the module-level handle so `applyGarment` can reach it, and
  // held locally too because the closures below cannot narrow a `let`.
  const save = await installAutosave({
    stack,
    // Both, so a save carries the outfit rather than the half of it on screen.
    stacks,
    open,
    onRestored({ restored, dropped }) {
      if (dropped.length === 0) return;
      // The only honest place to say "four layers from this project no longer
      // exist" is in front of the person who made them.
      setSaveState({
        kind: "error",
        message: `Reopened ${restored} ${restored === 1 ? "layer" : "layers"}; could not restore ${dropped.join(", ")}.`,
      });
    },
  });
  autosave = save;

  /*
   * Somewhere for the first stroke to land.
   *
   * Keyed on the stack actually being empty rather than on "nothing was
   * restored". They differ in two cases that both matter: an archive that
   * legitimately contains no layers — someone deleted them all and saved — and
   * one that opened but whose layers could not be rebuilt. In both, `restored`
   * is false yet the document is real, and in both the editor still needs a
   * layer or `activeCtx()` is null and painting silently does nothing, which
   * looks exactly like the brush being broken.
   */
  if (stack.layers().length === 0) stack.addArtwork({ name: "Artwork" });

  /*
   * Apply whatever the restored document says it is.
   *
   * `persist: false` — this is boot reading the file, not the user changing it.
   * Writing it back would mark a document dirty for agreeing with itself, and
   * every rule about not saving an untouched design depends on that not
   * happening.
   */
  await applyGarment(save.garment(), false);

  /*
   * The name in the app bar is the name in the file.
   *
   * These were two different strings: the bar showed "Untitled shirt" while the
   * archive recorded "Untitled design", so a design the user had deliberately
   * renamed still arrived in their Designs list as Untitled. The export path
   * already reads this input for the filename, which makes it the name — it
   * just was not reaching the thing that stores it.
   */
  const nameInput = document.querySelector<HTMLInputElement>(".rename");
  if (nameInput) {
    // The file's name wins, always — including on a fresh document, where it is
    // the default. Pushing the input's value the other way at boot would call
    // setTitle(), which marks the document dirty, which would save an untouched
    // design the moment somebody opened the editor: exactly the bug that arming
    // `touchSave` late exists to prevent.
    nameInput.value = save.title();
    /*
     * `change`, not `input`: a save per keystroke would write an archive for
     * every letter of a rename, and the controller's debounce coalesces edits,
     * not identities. `change` already covers both ways a person finishes
     * typing — Enter, and clicking away — so neither needs its own handler.
     */
    nameInput.addEventListener("change", () => save.setTitle(nameInput.value));
    nameInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        // Commit and get out of the field, so the next keystroke is a shortcut
        // rather than more letters in the name. `change` fires on blur, and
        // fires only if the value actually moved, so this cannot double-save.
        nameInput.blur();
        return;
      }
      if (e.key === "Escape") {
        // Put it back and leave. Without this the only way out of a half-typed
        // rename is to remember what it used to say and type it again —
        // Escape is the one key everybody tries, and it did nothing.
        nameInput.value = save.title();
        nameInput.blur();
      }
    });
  }

  /*
   * Shirt or pants, in the bar beside the name.
   *
   * This is a document property, not an export option, so it cannot wait for
   * the export step: the guide picture and the 3D preview have to agree with
   * what you are drawing *while* you draw it. The export step confirms the
   * choice rather than making it — see work-management-and-export.md §2.2.
   *
   * A native <select> because there are two options and no reason to
   * reimplement a listbox: keyboard, screen readers and the mobile picker all
   * come free and correct.
   */
  /*
   * Cast rather than `querySelector<HTMLSelectElement>`.
   *
   * `tsconfig` pulls in `@cloudflare/workers-types` for the worker, whose
   * HTMLRewriter `Element` shadows the DOM one that `querySelector`'s generic is
   * constrained to — and `HTMLSelectElement` is the one element type that fails
   * the resulting check. `HTMLInputElement` and `HTMLTextAreaElement` both pass,
   * which is why nothing has hit this before. Verified, not guessed.
   */
  const garmentSelect = document.querySelector(".garment-pick") as HTMLSelectElement | null;
  if (garmentSelect) {
    garmentSelect.value = save.garment();
    garmentSelect.addEventListener("change", async () => {
      await applyGarment(garmentSelect.value === "pants" ? "pants" : "shirt", true);
      // An unnamed design takes its garment's name, so the box has to be told.
      // Reading it back rather than deriving it keeps the file the one source.
      if (nameInput) nameInput.value = save.title();
    });
  }

  // Set once. An empty wet canvas draws nothing, so there is no start/stop
  // bookkeeping that could leave a stroke stranded or a preview stuck on.
  stack.setStrokeOverlay(painter.wetCanvas, {
    opacity: painter.opacity,
    mode: painter.mode,
    active: painter.hasStroke,
  });
  renderPanel();

  /*
   * Only now does a change count as an edit.
   *
   * Everything above — the restore, the seeded blank layer, the overlay, the
   * first composite — routes through `onChange` like any other mutation. Arming
   * the save before them would mean that opening the editor and touching
   * nothing writes an "Untitled design" to this device, and, for anyone signed
   * in, creates a cloud project: one of a free plan's three, spent on a blank
   * canvas the person never drew on.
   *
   * So the arming line is the boundary between "the editor set itself up" and
   * "the user did something", and it belongs after the setup.
   */
  touchSave = save.touch;
  bindPresenter(paintCanvas); // draws once immediately
  wirePainting(paintCanvas);
  // Before anything can toast: a live region created alongside its first
  // message is frequently never announced.
  installToaster();
  wireTools();
  wireImport();
  wireExport();
  wireVersions(save);

  if (!mount) return;
  if (!webglSupported()) {
    showFallback("3D preview needs WebGL. The 2D editor works without it.");
    setLabel("no WebGL");
    return;
  }

  const mq = window.matchMedia(CAN_SHOW_3D);
  const sync = (matches: boolean) => (matches ? void mountViewer() : unmountViewer());
  sync(mq.matches);
  mq.addEventListener("change", (e) => sync(e.matches));
}

/**
 * Mount the saved-versions list into the Source dock.
 *
 * Scaffold — the list, its states and Restore. Kept out of `boot`'s body
 * because the panel owns a fetch and an AbortController, and boot is already
 * the longest function here.
 *
 * The unpack happens in this file rather than in the panel: `restoreVersion`
 * wants a `Project` and its blobs, and the panel has no business knowing the
 * archive format. It hands over a version id and gets a promise back.
 */
function wireVersions(save: NonNullable<typeof autosave>) {
  /*
   * Cast rather than `querySelector<HTMLElement>`, and `replace` rather than
   * `slot.append`. `tsconfig` pulls in `@cloudflare/workers-types` for the
   * worker, whose HTMLRewriter `Element` shadows the DOM one — its `append`
   * takes a string or a Response, so passing a node is an error. Same trap as
   * the garment `<select>` below.
   */
  const slot = document.querySelector("[data-versions-slot]") as HTMLElement | null;
  if (!slot) return;

  const panel = createVersionsPanel({
    status: () => save.cloudStatus(),
    tier: () => identity().tier,
    signedIn: () => identity().signedIn || identity().devUser !== null,
    async onRestore(versionId) {
      const status = save.cloudStatus();
      // The panel only shows Restore when there was a link, but it re-reads on
      // click and a sign-out in between is a real sequence.
      if (status.kind !== "linked") {
        throw new Error("This design is not in your account any more.");
      }
      const id = status.projectId;

      const [{ loadBlob }, { unpackProject }] = await Promise.all([
        import("../api/projects"),
        import("../../shared/pack"),
      ]);
      const { bytes } = await loadBlob(id, versionId);
      const { project, blobs } = unpackProject(bytes);
      const info = await restoreVersion(project, blobs);
      /*
       * A toast, not the save indicator.
       *
       * The indicator means "is my work safe" and the restore has already
       * queued its own save, which will drive it honestly a moment from now.
       * Writing a fake "saved" into it would be claiming an outcome that has
       * not happened yet.
       */
      toast(
        info.dropped.length === 0
          ? "Version restored. Undo puts it back."
          : `Restored ${info.restored} of ${info.restored + info.dropped.length} layers.`,
        { tone: info.dropped.length === 0 ? "success" : "warn" },
      );
    },
  });

  replace(slot, panel.el);
  /*
   * Re-read whenever the cloud copy moves.
   *
   * A save creates a version; signing out empties the list; a refusal changes
   * what the panel should say. The panel defers the fetch itself when nothing
   * can see it, so this can fire freely.
   */
  save.onCloudChange(() => panel.refresh());
}

/*
  `void boot()` swallowed rejections silently, which meant a failure anywhere in
  startup looked identical to "nothing happened" — an empty 3D pane and a blank
  canvas with no clue why. Startup failures have to be loud.
*/
boot().catch((err) => {
  console.error("editor failed to start", err);
  showFallback("Editor failed to start");
});
